/**
 * Entry point.
 *
 * Phase A: choose / drag-drop a PDF, validate it, show name and size.
 * Phase B: PDF.js text extraction and raw debug data.
 * Phase C: layout analysis → lines → blocks → translation blocks.
 * Translation Test Mode: pick 1–10 blocks, translate only those, review quality.
 * Full translation: unlocked only after the test is marked as passed.
 * Phase D–F: pdf-lib overlay → downloadable <name>_zh-TW.pdf (or a debug
 * bounding-box PDF to verify coordinates first).
 *
 * User Mode (default): choosing a file runs analyze → chapter detection →
 * translation scope (whole document / chapters / page range) → quote →
 * 確認價格並繼續 → payment (flow/lifecycle.ts + payment/*, bypassed while
 * PAYMENT_MODE = 'disabled') → translate the selected final logical units →
 * side-by-side bilingual PDF, with simple progress only. Nothing is sent to
 * the Worker before the confirmed quote has passed the payment gate.
 * Developer Mode (?debug=true): every tool above, unchanged; the scope applies
 * to Translate Full PDF and the export. Both modes use the same analyzePdf() /
 * resolveTranslationScope() / translateAll() / buildPdf() core.
 *
 * The PDF is read with the File API into an ArrayBuffer and never uploaded.
 * Only JSON text blocks (plus an optional terminology map) go to the Worker.
 * The same ArrayBuffer is kept for pdf-lib; PDF.js only ever sees a copy.
 */

import './styles.css';
import { extractPdf } from './pdf/extract';
import { FONT_SOURCES, FontLoadError, loadFontSet } from './pdf/font';
import { analyzeLayout } from './pdf/layout';
import { TYPOGRAPHY } from './pdf/typography';
import {
  assessOverlay,
  generateTranslatedPdf,
  RenderError,
  type RenderMode,
  type RenderOutput,
  type RenderResult,
} from './pdf/render';
import type { FontSetBytes } from './pdf/font';
import type { RenderProgress } from './pdf/render';
import type { LayoutResult, LayoutRole, PdfAnalysis, TextBlock, TextItemDebug, TranslationBlock, TranslationEntry } from './pdf/types';
import { ROLE_LABELS_ZH, roleOf } from './pdf/roles';
import { SIDEBAR_MIN_CONFIDENCE } from './pdf/detectors/sidebar';
import { STRUCTURED_MIN_LABEL_CONFIDENCE, STRUCTURED_MIN_REGION_CONFIDENCE } from './pdf/detectors/structuredAbstract';
import type { TranslationStats } from './translate/batch';
import { TranslationCache } from './translate/cache';
import { TranslateClient, type WorkerUsage } from './translate/client';
import { chapterPageLabel, chapterSubtreeIds, detectChapters, type ChapterDetection, type ChapterInfo } from './scope/chapters';
import { resolveOutputPages, withOutputSuffix, type OutputPages } from './scope/output';
import {
  resolveTranslationScope,
  terminologyContextUnits,
  unitsInDocumentOrder,
  type ScopeResolution,
  type TranslationScope,
} from './scope/scope';
import { clearTerminologyCache, translateDocument, type DocumentTranslationOptions, type DocumentTranslationResult } from './translate/pipeline';
import { clearSession, getSessionToken, saveSession } from './translate/session';
import { documentFingerprint, fnv1a } from './translate/terminology';
import { JobUsage, formatTokens, formatUsageBreakdown } from './translate/usage';
import { WORKER_URL } from './config';
import { buildQuote, type QuoteSnapshot } from './pricing/quote';
import { validateQuote, type PricingValidation } from './pricing/validation';
import { historyToCsv, PricingHistory, summarizeHistory, toRecord } from './pricing/history';
import { formatInt, historyLines, quoteDevLines, userQuoteText, validationLines } from './pricing/format';
import { QuoteExpiredError, TranslationJobLifecycle, type JobContext } from './flow/lifecycle';
import { flowDevLines, flowDevSummary, gateFailureMessage, MSG_CONFIRMING, MSG_PAYMENT_NOT_CONFIRMED, MSG_QUOTE_EXPIRED } from './flow/format';
import { PAYMENT_MODE } from './payment/config';
import { createPaymentProvider } from './payment/provider';

const DEBUG_ITEM_LIMIT = 50;
const TARGET_LANGUAGE = 'zh-TW';
const TEST_MIN_BLOCKS = 1;
const TEST_MAX_BLOCKS = 10;
const SAMPLE_SIZE = 8;

/**
 * ?debug=true shows every developer tool; otherwise the simplified User Mode.
 * Development builds only (`npm run dev`): production builds ignore ?debug=true,
 * and their index.html has no dev-only elements at all (see vite.config.ts).
 */
const DEBUG_MODE = import.meta.env.DEV && new URLSearchParams(window.location.search).get('debug') === 'true';
document.documentElement.classList.toggle('debug', DEBUG_MODE);

// ---------------------------------------------------------------------------
// DOM lookup
// ---------------------------------------------------------------------------

function byId<T extends HTMLElement>(id: string): T {
  const el = document.getElementById(id);
  if (!el) throw new Error(`Missing element #${id}`);
  return el as T;
}

/**
 * Developer Mode elements (inside a `dev-only` element in index.html). Production
 * builds strip them from the page, so a missing one falls back to a detached
 * element of the same tag: the code below can keep updating it, nothing shows it.
 */
function devById<K extends keyof HTMLElementTagNameMap>(id: string, tag: K): HTMLElementTagNameMap[K] {
  return (document.getElementById(id) as HTMLElementTagNameMap[K] | null) ?? document.createElement(tag);
}

const dropZone = byId<HTMLDivElement>('drop-zone');
const fileInput = byId<HTMLInputElement>('file-input');
const chooseBtn = byId<HTMLButtonElement>('choose-btn');
const fileInfo = byId<HTMLDivElement>('file-info');
const fileNameEl = byId<HTMLElement>('file-name');
const fileSizeEl = byId<HTMLElement>('file-size');
const analyzeBtn = devById('analyze-btn', 'button');
const statusEl = devById('status', 'p');
const errorEl = byId<HTMLParagraphElement>('error');

const resultsEl = devById('results', 'section');
const resFile = devById('res-file', 'dd');
const resPages = devById('res-pages', 'dd');
const resItems = devById('res-items', 'dd');
const resSelectable = devById('res-selectable', 'dd');
const resLayout = devById('res-layout', 'dd');
const resBodyFont = devById('res-body-font', 'dd');
const resBlocks = devById('res-blocks', 'dd');
const resTranslationBlocks = devById('res-translation-blocks', 'dd');
const resContext = devById('res-context', 'dd');
const warningEl = devById('warning', 'p');
const unsupportedEl = devById('unsupported', 'p');

const toolbar = devById('analysis-toolbar', 'div');
const blocksToggle = devById('blocks-toggle', 'button');
const debugToggle = devById('debug-toggle', 'button');
const workerInfo = devById('worker-info', 'p');

const blocksPanel = devById('blocks-panel', 'div');
const blocksOnlyTranslate = devById('blocks-only-translate', 'input');
const blocksList = devById('blocks-list', 'div');

const typographyDiagnostics = devById('typography-diagnostics', 'details');
const typographyDiagnosticsSummary = devById('typography-diagnostics-summary', 'summary');
const typographyDiagnosticsUnits = devById('typography-diagnostics-units', 'input');
const typographyDiagnosticsList = devById('typography-diagnostics-list', 'pre');
const tableDiagnostics = devById('table-diagnostics', 'details');
const tableDiagnosticsSummary = devById('table-diagnostics-summary', 'summary');
const tableDiagnosticsCells = devById('table-diagnostics-cells', 'input');
const tableDiagnosticsList = devById('table-diagnostics-list', 'pre');
const roleDiagnostics = devById('role-diagnostics', 'details');
const roleDiagnosticsSummary = devById('role-diagnostics-summary', 'summary');
const roleDiagnosticsBlocks = devById('role-diagnostics-blocks', 'input');
const roleDiagnosticsOverlay = devById('role-diagnostics-overlay', 'input');
const roleDiagnosticsList = devById('role-diagnostics-list', 'pre');
const debugPanel = devById('debug-panel', 'div');
const debugLimitEl = devById('debug-limit', 'span');
const debugList = devById('debug-list', 'div');

// test mode
const testSection = devById('test-section', 'section');
const terminologyInput = devById('terminology-input', 'textarea');
const terminologyError = devById('terminology-error', 'p');
const pickSampleBtn = devById('pick-sample-btn', 'button');
const clearSelectionBtn = devById('clear-selection-btn', 'button');
const pickAbstractBtn = devById('pick-abstract-btn', 'button');
const testBtn = devById('test-btn', 'button');
const selectionInfo = devById('selection-info', 'span');
const testStatus = devById('test-status', 'p');
const testList = devById('test-list', 'div');
const testOkWrapper = devById('test-ok-wrapper', 'label');
const testOkCheckbox = devById('test-ok-checkbox', 'input');

// full translation
const fullSection = devById('full-section', 'section');
const translateBtn = devById('translate-btn', 'button');
const retryBtn = devById('retry-btn', 'button');
const translateStatus = devById('translate-status', 'p');
const costStats = devById('cost-stats', 'details');
const costStatsList = devById('cost-stats-list', 'pre');
const terminologyPanel = devById('terminology-panel', 'details');
const terminologySummary = devById('terminology-summary', 'summary');
const terminologyList = devById('terminology-list', 'pre');
const qaPanel = devById('qa-panel', 'details');
const qaSummary = devById('qa-summary', 'summary');
const qaList = devById('qa-list', 'pre');
const previewEl = devById('preview', 'section');
const previewSummary = devById('preview-summary', 'p');
const previewList = devById('preview-list', 'div');

// PDF export (Phase D–F)
const exportSection = devById('export-section', 'section');
const exportSummary = devById('export-summary', 'p');
const exportMode = devById('export-mode', 'select');
const renderRange = devById('render-range', 'select');
const renderDebug = devById('render-debug', 'input');
const generateBtn = devById('generate-btn', 'button');
const renderStatus = devById('render-status', 'p');
const renderProgress = devById('render-progress', 'progress');
const renderError = devById('render-error', 'p');
const fontNote = devById('font-note', 'p');
const renderWarnings = devById('render-warnings', 'details');
const renderWarningsSummary = devById('render-warnings-summary', 'summary');
const renderWarningsList = devById('render-warnings-list', 'pre');
const downloadLink = devById('download-link', 'a');

// User Mode
const jobCard = byId<HTMLElement>('job-card');
const jobProgress = byId<HTMLDivElement>('job-progress');
const jobStage = byId<HTMLParagraphElement>('job-stage');
const jobPercent = byId<HTMLElement>('job-percent');
const jobBar = byId<HTMLProgressElement>('job-bar');
const jobSteps = byId<HTMLOListElement>('job-steps');
const jobTitle = byId<HTMLParagraphElement>('job-title');
const jobPhase = byId<HTMLParagraphElement>('job-phase');
const jobDone = byId<HTMLDivElement>('job-done');
const jobNote = byId<HTMLParagraphElement>('job-note');
const jobOutput = byId<HTMLParagraphElement>('job-output');
const jobUsageEl = byId<HTMLParagraphElement>('job-usage');
const jobDownload = byId<HTMLAnchorElement>('job-download');
const jobError = byId<HTMLParagraphElement>('job-error');
const jobReset = byId<HTMLButtonElement>('job-reset');
const jobRescope = byId<HTMLButtonElement>('job-rescope');
const jobRetry = byId<HTMLButtonElement>('job-retry');
const uploadNote = byId<HTMLParagraphElement>('upload-note');

// Translation scope (both modes)
const scopeCard = byId<HTMLElement>('scope-card');
const scopeModeInputs = [...scopeCard.querySelectorAll<HTMLInputElement>('input[name="scope-mode"]')];
const scopeChaptersPanel = byId<HTMLDivElement>('scope-chapters');
const scopeChapterCount = byId<HTMLParagraphElement>('scope-chapter-count');
const chapterTree = byId<HTMLDivElement>('chapter-tree');
const scopePagesPanel = byId<HTMLDivElement>('scope-pages');
const scopeStartInput = byId<HTMLInputElement>('scope-start');
const scopeEndInput = byId<HTMLInputElement>('scope-end');
const scopePageTotal = byId<HTMLElement>('scope-page-total');
const scopeFullWrap = byId<HTMLLabelElement>('scope-full-wrap');
const scopeFull = byId<HTMLInputElement>('scope-full');
const scopeOutput = byId<HTMLParagraphElement>('scope-output');
const scopeError = byId<HTMLParagraphElement>('scope-error');
const scopeStartBtn = byId<HTMLButtonElement>('scope-start-btn');
const scopeReset = byId<HTMLButtonElement>('scope-reset');
const scopeDevSummary = devById('scope-dev-summary', 'summary');
const scopeDevList = devById('scope-dev-list', 'pre');

// Quote (both modes) and pricing validation (Developer Mode only)
const quoteEl = byId<HTMLDivElement>('quote');
const quoteChars = byId<HTMLParagraphElement>('quote-chars');
const quotePrice = byId<HTMLParagraphElement>('quote-price');
const quotePriceLabel = byId<HTMLParagraphElement>('quote-price-label');
const quoteNote = byId<HTMLParagraphElement>('quote-note');
const quoteTestPhase = byId<HTMLParagraphElement>('quote-test-phase');
const quoteDetailSummary = byId<HTMLElement>('quote-detail-summary');
const quoteDetailText = byId<HTMLParagraphElement>('quote-detail-text');
const quoteDev = devById('quote-dev', 'details');
const quoteDevSummary = devById('quote-dev-summary', 'summary');
const quoteDevList = devById('quote-dev-list', 'pre');
const pricingSection = devById('pricing-section', 'section');
const pricingValidationPanel = devById('pricing-validation', 'details');
const pricingValidationSummary = devById('pricing-validation-summary', 'summary');
const pricingValidationList = devById('pricing-validation-list', 'pre');
const pricingHistorySummary = devById('pricing-history-summary', 'summary');
const pricingHistoryList = devById('pricing-history-list', 'pre');
const pricingHistoryInfo = devById('pricing-history-info', 'span');
const pricingExportBtn = devById('pricing-export-btn', 'button');
const pricingClearBtn = devById('pricing-clear-btn', 'button');
// Job lifecycle / payment flow (Developer Mode panel)
const flowDev = devById('flow-dev', 'details');
const flowDevSummaryEl = devById('flow-dev-summary', 'summary');
const flowDevList = devById('flow-dev-list', 'pre');

// Invite gate
const inviteForm = byId<HTMLFormElement>('invite-form');
const inviteInput = byId<HTMLInputElement>('invite-input');
const inviteSubmit = byId<HTMLButtonElement>('invite-submit');
const inviteError = byId<HTMLParagraphElement>('invite-error');

// ---------------------------------------------------------------------------
// State (all in-memory, browser only)
// ---------------------------------------------------------------------------

let currentFile: File | null = null;
/** The untouched file bytes; PDF.js works on a copy, pdf-lib on these. */
let currentPdfBytes: ArrayBuffer | null = null;
let currentAnalysis: PdfAnalysis | null = null;
let currentLayout: LayoutResult | null = null;
let entries = new Map<string, TranslationEntry>();
let selectedIds = new Set<string>();
let isAnalyzing = false;
let isTranslating = false;
let isGenerating = false;
/** Object URL of the last generated PDF, revoked when replaced or reset. */
let generatedUrl: string | null = null;
let providerLabel = 'OpenAI';
/** Last PDF export, for the table diagnostics (overflow / fallback cells). */
let lastRenderResult: RenderResult | null = null;
/** Chapters of the current PDF (native outline first, HEADING units otherwise); null before analysis. */
let chapterDetection: ChapterDetection | null = null;
/** Ticked chapter checkboxes (a parent is included once all its children are ticked). */
let checkedChapterIds = new Set<string>();
/** Scope of the last translation run: drives the PDF export and the Developer Mode panel. */
let lastScope: ScopeResolution | null = null;
/** Pages the last export wrote, for the "輸出頁面" line under the download button. */
let lastOutput: OutputPages | null = null;
/** Local validation dataset (statistics only, newest 50, localStorage). */
const pricingHistory = new PricingHistory();

/**
 * Job lifecycle (flow/lifecycle.ts): ANALYZING → QUOTE_READY → QUOTE_CONFIRMED →
 * PAYMENT_PENDING → PAYMENT_CONFIRMED → TRANSLATING → RENDERING → COMPLETED.
 * lifecycle.startTranslation() is the only way into a provider call of a paid
 * run (User Mode 確認價格並繼續, Developer Mode Translate Full PDF); the
 * payment provider is the PAYMENT_MODE one ('disabled': explicit bypass).
 */
const lifecycle = new TranslationJobLifecycle();
const paymentProvider = createPaymentProvider(PAYMENT_MODE);

/**
 * One job per selected file and per translation run (開始翻譯 / Translate Full
 * PDF): a new file or a new scope run aborts the previous job, so its
 * remaining batches are not sent and none of its results reach the UI.
 */
interface Job {
  id: number;
  controller: AbortController;
}
let jobCounter = 0;
let currentJob: Job | null = null;

/**
 * Token usage of the current job. Every provider call of this file (terminology,
 * translation incl. retries, QA) adds to it; a new file starts at zero.
 */
let jobUsage = new JobUsage();

function startJob(): Job {
  currentJob?.controller.abort();
  currentJob = { id: ++jobCounter, controller: new AbortController() };
  jobUsage = new JobUsage(); // per-job counters: a new PDF or scope run never inherits earlier tokens
  return currentJob;
}

const cache = new TranslationCache();
const client = new TranslateClient(WORKER_URL, undefined, {
  getToken: getSessionToken,
  onUnauthorized: () => lockApp(MSG_SESSION_EXPIRED),
});

// ---------------------------------------------------------------------------
// Helpers
// ---------------------------------------------------------------------------

function formatBytes(bytes: number): string {
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
  return `${(bytes / (1024 * 1024)).toFixed(2)} MB`;
}

function setText(el: HTMLElement, message: string | null): void {
  if (message === null) {
    el.hidden = true;
    el.textContent = '';
  } else {
    el.hidden = false;
    el.textContent = message;
  }
}

const setStatus = (m: string | null) => setText(statusEl, m);
const setError = (m: string | null) => setText(errorEl, m);
const setTranslateStatus = (m: string | null) => setText(translateStatus, m);
const setTestStatus = (m: string | null) => setText(testStatus, m);

function el<K extends keyof HTMLElementTagNameMap>(
  tag: K,
  className?: string,
  text?: string,
): HTMLElementTagNameMap[K] {
  const node = document.createElement(tag);
  if (className) node.className = className;
  if (text !== undefined) node.textContent = text;
  return node;
}

function translationLabel(): string {
  return `${providerLabel} Translation:`;
}

/**
 * Parse the terminology textarea. Accepts either a JSON object or one
 * `english term = 中文` (or `english → 中文`) pair per line.
 */
function parseTerminology(raw: string): { map: Record<string, string>; error: string | null } {
  const text = raw.trim();
  if (!text) return { map: {}, error: null };

  if (text.startsWith('{')) {
    try {
      const obj = JSON.parse(text) as unknown;
      if (!obj || typeof obj !== 'object' || Array.isArray(obj)) return { map: {}, error: 'JSON must be an object.' };
      const map: Record<string, string> = {};
      for (const [k, v] of Object.entries(obj as Record<string, unknown>)) {
        if (typeof v !== 'string') return { map: {}, error: `Value for "${k}" must be a string.` };
        if (k.trim() && v.trim()) map[k.trim()] = v.trim();
      }
      return { map, error: null };
    } catch (err) {
      return { map: {}, error: `Invalid JSON: ${err instanceof Error ? err.message : String(err)}` };
    }
  }

  const map: Record<string, string> = {};
  const lines = text.split(/\r?\n/);
  for (const [i, line] of lines.entries()) {
    const t = line.trim();
    if (!t || t.startsWith('#')) continue;
    const m = /^(.+?)\s*(?:=|→|->|:)\s*(.+)$/.exec(t);
    if (!m) return { map: {}, error: `Line ${i + 1}: expected "english term = 中文".` };
    map[m[1].trim()] = m[2].trim();
  }
  return { map, error: null };
}

function currentTerminology(): Record<string, string> | null {
  const { map, error } = parseTerminology(terminologyInput.value);
  setText(terminologyError, error);
  return error ? null : map;
}

function resetResults(): void {
  currentPdfBytes = null;
  currentAnalysis = null;
  currentLayout = null;
  entries = new Map();
  selectedIds = new Set();
  resetScopeUi();
  lastScope = null;
  resetQuoteUi();
  lifecycle.reset(); // the confirmed quote, payment and entitlement belonged to the previous PDF
  clearTerminologyCache(); // the scope-keyed auto glossary belongs to the previous PDF
  resetExport();
  resultsEl.hidden = true;
  warningEl.hidden = true;
  unsupportedEl.hidden = true;
  toolbar.hidden = true;
  blocksPanel.hidden = true;
  blocksList.replaceChildren();
  blocksToggle.textContent = 'Show Translation Blocks';
  debugPanel.hidden = true;
  debugList.replaceChildren();
  debugToggle.textContent = 'Show Debug Data';
  tableDiagnostics.hidden = true;
  tableDiagnosticsList.textContent = '';
  typographyDiagnostics.hidden = true;
  typographyDiagnosticsList.textContent = '';
  lastRenderResult = null;

  testSection.hidden = true;
  testList.replaceChildren();
  setTestStatus(null);
  testOkWrapper.hidden = true;
  testOkCheckbox.checked = false;

  fullSection.hidden = true;
  costStats.hidden = true;
  costStatsList.textContent = '';
  terminologyPanel.hidden = true;
  terminologyList.textContent = '';
  qaPanel.hidden = true;
  qaList.textContent = '';
  translateBtn.disabled = true;
  retryBtn.hidden = true;
  setTranslateStatus(null);
  previewEl.hidden = true;
  previewList.replaceChildren();
  updateSelectionUi();
}

/**
 * Accept a file only if it claims to be a PDF (MIME type or .pdf extension)
 * AND its first bytes contain the "%PDF-" signature.
 */
async function isProbablyPdf(file: File): Promise<boolean> {
  const claimsPdf = file.type === 'application/pdf' || /\.pdf$/i.test(file.name);
  if (!claimsPdf) return false;
  const head = new Uint8Array(await file.slice(0, 1024).arrayBuffer());
  return new TextDecoder('latin1').decode(head).includes('%PDF-');
}

// ---------------------------------------------------------------------------
// Worker info
// ---------------------------------------------------------------------------

async function refreshWorkerInfo(): Promise<void> {
  workerInfo.textContent = `Worker endpoint: ${client.endpoint} (checking...)`;
  const health = await client.workerInfo();
  if (!health) {
    workerInfo.textContent = `Worker endpoint: ${client.endpoint} — not reachable. Start \`wrangler dev\` in worker/.`;
    return;
  }
  const provider = health.provider ?? 'unknown';
  providerLabel = provider === 'openai' ? 'OpenAI' : provider === 'anthropic' ? 'Claude' : provider;
  workerInfo.textContent =
    `Worker endpoint: ${client.endpoint} · provider: ${provider} · model: ${health.model ?? '?'} · reasoning: ${health.effort ?? '?'}`;
}

// ---------------------------------------------------------------------------
// Invite gate: the Worker checks the code and issues a signed session token.
// This screen is only a convenience; /translate itself rejects requests
// without a valid token, so hiding the gate in the browser unlocks nothing.
// ---------------------------------------------------------------------------

const MSG_INVITE_INVALID = '邀請碼不正確';
const MSG_INVITE_RATE_LIMITED = '嘗試次數過多，請稍後再試。';
const MSG_SESSION_EXPIRED = '登入已過期，請重新輸入邀請碼。';
const MSG_NETWORK = '目前無法連線至翻譯服務，請稍後再試。';

function isLocked(): boolean {
  return document.documentElement.classList.contains('locked');
}

function unlockApp(): void {
  document.documentElement.classList.remove('locked');
  setText(inviteError, null);
  inviteInput.value = '';
  if (DEBUG_MODE) void refreshWorkerInfo();
}

/** Back to the invite screen. Called on any 401; stops the running job so no more batches are sent. */
function lockApp(message: string | null): void {
  clearSession();
  const wasLocked = isLocked();
  document.documentElement.classList.add('locked');
  if (message) setText(inviteError, message);
  if (wasLocked) return;
  if (!DEBUG_MODE) {
    currentJob?.controller.abort();
    currentJob = null;
    resetJobUi();
    fileInfo.hidden = true;
    currentFile = null;
  }
  inviteInput.focus();
}

inviteForm.addEventListener('submit', (event) => {
  event.preventDefault();
  void submitInvite();
});

async function submitInvite(): Promise<void> {
  const code = inviteInput.value.trim();
  if (!code) {
    setText(inviteError, '請輸入邀請碼。');
    inviteInput.focus();
    return;
  }
  inviteSubmit.disabled = true;
  setText(inviteError, null);
  try {
    const result = await client.verifyInvite(code);
    if (result.ok) {
      saveSession(result.token, result.expiresAt);
      unlockApp();
      return;
    }
    setText(
      inviteError,
      result.reason === 'invalid' ? MSG_INVITE_INVALID : result.reason === 'rate_limited' ? MSG_INVITE_RATE_LIMITED : MSG_NETWORK,
    );
    if (result.reason === 'invalid') inviteInput.select();
  } finally {
    inviteSubmit.disabled = false;
  }
}

/** On load: a stored, unexpired token opens the app; the Worker confirms it in the background. */
async function initSession(): Promise<void> {
  if (!getSessionToken()) {
    clearSession();
    inviteInput.focus();
    return;
  }
  unlockApp();
  if ((await client.checkSession()) === 'invalid') lockApp(MSG_SESSION_EXPIRED);
  // 'unknown' (Worker unreachable): stay unlocked; the first /translate call decides.
}

void initSession();

// ---------------------------------------------------------------------------
// Phase A: file selection
// ---------------------------------------------------------------------------

async function handleFile(file: File | undefined): Promise<void> {
  const job = startJob();
  setError(null);
  setStatus(null);
  resetResults();
  resetJobUi();
  if (!file) return;

  const isPdf = await isProbablyPdf(file);
  if (job !== currentJob) return; // another file was chosen meanwhile
  if (!isPdf) {
    currentFile = null;
    fileInfo.hidden = true;
    analyzeBtn.disabled = true;
    setError(DEBUG_MODE ? `"${file.name}" is not a PDF file. Only application/pdf is accepted.` : '請選擇 PDF 檔案（.pdf）。');
    return;
  }

  currentFile = file;
  fileNameEl.textContent = file.name;
  fileSizeEl.textContent = formatBytes(file.size);
  fileInfo.hidden = false;
  analyzeBtn.disabled = false;

  if (!DEBUG_MODE) void runAnalysisJob(file, job);
}

chooseBtn.addEventListener('click', () => fileInput.click());
jobReset.addEventListener('click', () => fileInput.click());
dropZone.addEventListener('click', (event) => {
  if (event.target !== chooseBtn) fileInput.click();
});
dropZone.addEventListener('keydown', (event) => {
  if (event.key === 'Enter' || event.key === ' ') {
    event.preventDefault();
    fileInput.click();
  }
});
fileInput.addEventListener('change', () => {
  void handleFile(fileInput.files?.[0]);
  fileInput.value = '';
});
for (const name of ['dragenter', 'dragover'] as const) {
  dropZone.addEventListener(name, (event) => {
    event.preventDefault();
    dropZone.classList.add('is-dragover');
  });
}
for (const name of ['dragleave', 'dragend'] as const) {
  dropZone.addEventListener(name, () => dropZone.classList.remove('is-dragover'));
}
dropZone.addEventListener('drop', (event) => {
  event.preventDefault();
  dropZone.classList.remove('is-dragover');
  void handleFile(event.dataTransfer?.files?.[0]);
});
window.addEventListener('dragover', (event) => event.preventDefault());
window.addEventListener('drop', (event) => event.preventDefault());

// ---------------------------------------------------------------------------
// Phase B + C: analysis
// ---------------------------------------------------------------------------

type AnalysisStep = { step: 'read' } | { step: 'extract'; done: number; total: number } | { step: 'layout' };

interface AnalyzedPdf {
  /** Untouched file bytes for pdf-lib. */
  buffer: ArrayBuffer;
  analysis: PdfAnalysis;
  /** Null when the PDF has no selectable text. Includes the translation blocks. */
  layout: LayoutResult | null;
}

/** Shared core: read the file, extract text, analyze layout, build translation blocks. */
async function analyzePdf(file: File, onStep: (s: AnalysisStep) => void): Promise<AnalyzedPdf> {
  onStep({ step: 'read' });
  // extractPdf() hands PDF.js a copy (the worker transfer would detach it);
  // these original bytes are what pdf-lib loads later.
  const buffer = await file.arrayBuffer();
  onStep({ step: 'extract', done: 0, total: 0 });
  const analysis = await extractPdf(buffer, file.name, {
    onProgress: (done, total) => onStep({ step: 'extract', done, total }),
  });

  let layout: LayoutResult | null = null;
  if (analysis.hasSelectableText) {
    onStep({ step: 'layout' });
    await new Promise((r) => requestAnimationFrame(r));
    layout = analyzeLayout(analysis);
    assessOverlay(layout, analysis);
  }
  logAnalysis(analysis, layout);
  return { buffer, analysis, layout };
}

async function runAnalysis(): Promise<void> {
  if (!currentFile || isAnalyzing) return;
  isAnalyzing = true;
  analyzeBtn.disabled = true;
  setError(null);
  resetResults();
  lifecycle.startAnalysis();
  const file = currentFile;

  try {
    const { buffer, analysis, layout } = await analyzePdf(file, (s) => {
      if (s.step === 'read') setStatus('Reading file...');
      else if (s.step === 'extract') setStatus(s.total ? `Extracting text... page ${s.done} / ${s.total}` : 'Extracting text...');
      else setStatus('Analyzing layout...');
    });
    currentPdfBytes = buffer;
    currentAnalysis = analysis;
    currentLayout = layout;

    setStatus(null);
    renderResults(analysis, layout);
    if (layout && layout.stats.translationBlockCount > 0) {
      exportSection.hidden = false;
      updateExportUi();
      showScopeCard();
    }
  } catch (err) {
    console.error('[PDF Analysis] failed', err);
    setStatus(null);
    setError(`Failed to analyze PDF: ${err instanceof Error ? err.message : String(err)}`);
  } finally {
    isAnalyzing = false;
    analyzeBtn.disabled = currentFile === null;
  }
}

/** Console debug data, printed in both modes. */
function logAnalysis(analysis: PdfAnalysis, layout: LayoutResult | null): void {
    console.log('[PDF Analysis]', analysis);
    console.log('[PDF Layout]', layout);
    if (layout) {
      console.table(
        layout.blocks.map((b) => ({
          id: b.id,
          page: b.page,
          type: b.type,
          column: b.column,
          x: b.x,
          y: b.y,
          w: b.width,
          h: b.height,
          fs: b.fontSize,
          lines: b.lineCount,
          section: b.sectionType,
          blockType: b.blockType,
          translate: b.translate,
          skip: b.skipReason ?? '',
          text: b.text.slice(0, 60),
        })),
      );
    }
    const w = window as unknown as { __pdfDebug: PdfAnalysis; __pdfLayout: LayoutResult | null };
    w.__pdfDebug = analysis;
    w.__pdfLayout = layout;
}

analyzeBtn.addEventListener('click', () => void runAnalysis());

function describeLayout(layout: LayoutResult): string {
  const { twoColumnPages, singleColumnPages } = layout.stats;
  if (twoColumnPages === 0) return 'Single Column';
  if (singleColumnPages === 0) return 'Two Columns';
  const twoCol = layout.pages.filter((p) => p.layout === 'TWO_COLUMN').map((p) => p.pageNumber);
  return `Two Columns on ${twoColumnPages} page(s) [${twoCol.slice(0, 12).join(', ')}${twoCol.length > 12 ? ', …' : ''}], Single on ${singleColumnPages}`;
}

/**
 * Developer Mode (繁體中文): what the typography pass did — heading
 * hierarchy, superscript citation reconstruction, paragraph indent / spacing
 * and the sentence tails that were merged back into their paragraph. User
 * Mode never sees this panel (the whole card is `dev-only`).
 */
function renderTypographyDiagnostics(layout: LayoutResult, render: RenderResult | null): void {
  const st = layout.stats;
  const t = render?.stats.typography ?? null;
  const body = layout.bodyFontSize;
  const orphanBlocks = layout.blocks.filter((b) => b.orphanOf);
  const supBlocks = layout.blocks.filter((b) => b.superscripts?.length);
  const paragraphReports = render ? render.reports.filter((r) => r.typography) : [];
  const headingReports = paragraphReports.filter((r) => r.typography?.role === 'title' || r.typography?.role === 'heading');
  const flat = headingReports.filter((r) => (r.typography?.bodyRatio ?? 0) < TYPOGRAPHY.minHeadingBodyRatio - 0.01);

  typographyDiagnosticsSummary.textContent =
    `排版診斷：標題 ${t ? t.titles + t.headings : headingReports.length} 個` +
    (t ? ` · 加粗 ${t.boldDrawn} · 放大 ${t.sizeBoosted} · 觸底 ${t.floorApplied}` : '') +
    ` · 上標引用 ${t ? t.superscriptRuns : st.superscriptMarkerCount} 個` +
    ` · 殘句合併 ${st.orphanMergedCount} 個` +
    (st.orphanUnresolvedCount ? ` · 未解決殘句 ${st.orphanUnresolvedCount}` : '');

  const lines: string[] = [
    `【標題層級】內文基準字級（body）：${body} pt`,
    `  設定：TITLE 起始 ${TYPOGRAPHY.title.sizeScale}× body，下限 ${TYPOGRAPHY.minTitleBodyRatio}× body`,
    `        HEADING 起始 ${TYPOGRAPHY.heading.sizeScale}× body，下限 ${TYPOGRAPHY.minHeadingBodyRatio}× body`,
    `        BODY 維持原尺寸，最多縮到 ${TYPOGRAPHY.fit.bodyMinRatio}×`,
    t
      ? `  實際：TITLE ${t.titles} 個、HEADING ${t.headings} 個、加粗 ${t.boldDrawn} 個、` +
          `因層級不足而放大 ${t.sizeBoosted} 個、因空間不足觸到字級下限 ${t.floorApplied} 個`
      : '  實際：—（請先產生 PDF）',
    t ? `  標題∕內文最小字級比：${t.minHeadingBodyRatio || '—'}（需 ≥ ${TYPOGRAPHY.minHeadingBodyRatio}）` : '',
    flat.length ? `  ⚠ ${flat.length} 個標題與內文字級差異仍不足` : '  ✓ 沒有標題被縮到與內文相近',
    '',
    `【上標引用重建】`,
    `  來源偵測：${st.superscriptBlockCount} 個區塊共 ${st.superscriptMarkerCount} 個標記` +
      `（其中 ${st.superscriptGluedCount} 個來自黏連文字後處理）`,
    t
      ? `  實際繪製：${t.superscriptRuns} 個，分布於 ${t.superscriptUnits} 個翻譯單元；` +
          `補回被模型漏掉的句尾標記 ${t.superscriptRestored} 個`
      : '  實際繪製：—（請先產生 PDF）',
    `  樣式：字級 ${TYPOGRAPHY.superscript.scale}×、基線上移 ${TYPOGRAPHY.superscript.riseEm} em（不改變行高）`,
    '',
    `【段落縮排與間距】`,
    `  首行縮排：BODY ${TYPOGRAPHY.body.firstLineIndentEm} em（標題∕圖表標題∕表格儲存格不套用）` +
      (t ? `，實際套用 ${t.indentedParagraphs} 個段落` : ''),
    `  段前∕段後：BODY ${TYPOGRAPHY.body.spaceBeforeEm}∕${TYPOGRAPHY.body.spaceAfterEm} em、` +
      `HEADING ${TYPOGRAPHY.heading.spaceBeforeEm} em（下滑入標題上方空白，不壓縮字級）` +
      (t ? `，實際套用 ${t.spacedParagraphs} 個單元` : ''),
    `  間距上限：最多佔區塊高度 ${Math.round(TYPOGRAPHY.spacing.maxShare * 100)}%，標題下滑最多 ${TYPOGRAPHY.spacing.maxSlideEm} em`,
    `  中英混排：中文與英文∕數字間一個空格；標點、百分比、單位、引用標記前不插空格；行首行尾不留空白`,
    '',
    `【句尾殘句（orphan fragment）】`,
    `  合併回前一段：${st.orphanMergedCount} 個`,
    `  仍未解決：${st.orphanUnresolvedCount} 個（找不到安全的所屬段落，維持原狀）`,
  ];

  if (render) {
    const overflow = render.warnings.filter((r) => r.reason === 'TEXT_OVERFLOW' && r.typography);
    const clipped = render.warnings.filter((r) => r.reason === 'PAGE_OVERFLOW');
    lines.push(
      '',
      `【尚未解決的警告】`,
      `  文字溢出區塊：${overflow.length} 個`,
      `  落出頁面而未繪製：${clipped.length} 個`,
    );
    for (const r of overflow.slice(0, 12)) {
      lines.push(`    ${r.unitId}  p${r.page}  ${r.type}  ${r.fontSize} → ${r.finalFontSize} pt  ${r.message ?? ''}`);
    }
  }

  if (typographyDiagnosticsUnits.checked) {
    lines.push('', '【標題明細】');
    for (const r of headingReports) {
      const ty = r.typography;
      if (!ty) continue;
      lines.push(
        `  ${r.unitId}  p${r.page}  ${ty.role}  原 ${r.fontSize} pt → 起始 ${ty.startFontSize} pt → 繪製 ${r.finalFontSize} pt  ` +
          `(${ty.bodyRatio}× body, 下限 ${ty.minFontSize} pt${ty.bold ? ', 加粗' : ''})`,
      );
    }
    lines.push('', '【上標引用明細】');
    for (const b of supBlocks.slice(0, 60)) {
      lines.push(`  ${b.id}  p${b.page}  ${b.type}  標記 ${(b.superscripts ?? []).join(' / ')}`);
    }
    lines.push('', '【殘句合併明細】');
    for (const b of orphanBlocks) {
      lines.push(`  ${b.id}  p${b.page}  → ${b.orphanOf}  ${b.orphanReason ?? ''}
    ${JSON.stringify(b.text.slice(0, 80))}`);
    }
  }

  typographyDiagnosticsList.textContent = lines.filter((l) => l !== '').length ? lines.join('\n') : '';
  typographyDiagnostics.hidden = false;
}

/**
 * Developer Mode: detected tables and figures, their logical cells, the
 * numeric cells kept in English, and (after an export) the cells that
 * overflowed and fell back to English, with page / table or figure / row /
 * column / source text / final font size.
 */
function renderTableDiagnostics(layout: LayoutResult, render: RenderResult | null): void {
  const st = layout.stats;
  const cellReports = render ? render.reports.filter((r) => r.cell) : [];
  const overflow = cellReports.filter((r) => r.reason === 'TABLE_CELL_OVERFLOW');
  const written = render ? render.stats.tableCellsWritten : 0;
  const figureWritten = render ? render.stats.figureCellsWritten : 0;
  const figureOverflow = cellReports.filter((r) => r.reason === 'FIGURE_CELL_OVERFLOW');
  tableDiagnosticsSummary.textContent =
    `Tables: ${st.tableCount} · ${st.tableCellCount} cells · ${st.tableTranslatedCells} translated · ${st.tableNumericCells} numeric preserved` +
    (render ? ` · ${written} written · ${overflow.length} overflow → English` : '') +
    (st.tableUnresolvedCount ? ` · ${st.tableUnresolvedCount} unresolved` : '') +
    `  ||  Figures: ${st.figureCount} · ${st.figureCellCount} elements · ${st.figureTranslatedCells} translated · ${st.figureNumericCells} numeric preserved` +
    (render ? ` · ${figureWritten} written · ${figureOverflow.length} overflow → English` : '') +
    (st.figureUnresolvedCount ? ` · ${st.figureUnresolvedCount} unresolved` : '');

  const lines: string[] = [
    `detected tables:           ${st.tableCount}`,
    `detected cells:            ${st.tableCellCount}`,
    `translated cells:          ${st.tableTranslatedCells}  (translation units; the rest need no API call)`,
    `numeric preserved cells:   ${st.tableNumericCells}`,
    `unresolved tables:         ${st.tableUnresolvedCount}  (cell clustering failed; blocks kept in English)`,
    render ? `cells written:             ${written}` : 'cells written:             — (generate a PDF)',
    render ? `overflow cells:            ${overflow.length}` : 'overflow cells:            —',
    render ? `fallback-to-English cells: ${overflow.length}` : 'fallback-to-English cells: —',
    '',
    `detected figures:          ${st.figureCount}  (figure caption + vector cluster; other drawings keep their English)`,
    `detected elements:         ${st.figureCellCount}`,
    `translated elements:       ${st.figureTranslatedCells}`,
    `numeric preserved:         ${st.figureNumericCells}`,
    `unresolved figures:        ${st.figureUnresolvedCount}  (text could not be separated; kept in English)`,
    render ? `elements written:          ${figureWritten}` : 'elements written:          — (generate a PDF)',
    render ? `element overflow:          ${figureOverflow.length}` : 'element overflow:          —',
    '',
    `duplicate source items:    ${st.duplicateSourceItems}  (must be 0: no text item belongs to two units)`,
    '',
    'tables:',
  ];
  for (const t of layout.tables) {
    lines.push(
      t.resolved
        ? `  page ${t.page}  table ${t.tableId}  rows ${t.rows}  columns ${t.columns}  cells ${t.cells}  translated ${t.translatedCells}  numeric ${t.numericCells}  header ${t.headerCells}`
        : `  page ${t.page}  table ${t.tableId}  UNRESOLVED (${t.reason}) — blocks kept in English`,
    );
  }
  if (layout.figures.length) {
    lines.push('', 'figures:');
    for (const f of layout.figures) {
      lines.push(
        f.resolved
          ? `  page ${f.page}  figure ${f.figureId}  ${f.containers} box container(s)  ${f.structured ? 'grid' : 'boxes + loose labels'}  ` +
              `${f.textItems} text items → ${f.cells} elements  ${f.translatedCells} translated  ${f.numericCells} numeric`
          : `  page ${f.page}  figure ${f.figureId}  UNRESOLVED (${f.reason}) — text kept in English`,
      );
    }
  }
  if (overflow.length || figureOverflow.length) {
    lines.push('', 'overflow cells / elements (English kept):');
    for (const r of [...overflow, ...figureOverflow]) {
      const c = r.cell;
      if (!c) continue;
      lines.push(
        `  page ${r.page}  ${c.kind} ${c.tableId}  row ${c.row}  column ${c.column}  final font ${c.finalFontSize} pt  reason ${c.overflowReason}\n    source: ${c.sourceText}`,
      );
    }
  }
  if (tableDiagnosticsCells.checked) {
    lines.push('', 'cells:');
    const box = (r: { x: number; y: number; width: number; height: number }) => `[${r.x}, ${r.y}, ${r.width}×${r.height}]`;
    for (const b of layout.blocks) {
      const c = b.cell;
      if (!c) continue;
      const final = cellReports.find((r) => r.sourceBlockIds[0] === b.id)?.cell?.finalFontSize;
      const span = c.colSpan > 1 ? `–${c.columnIndex + c.colSpan - 1}` : '';
      lines.push(
        `  ${c.id}  page ${c.page}  ${c.kind} ${c.tableId}  r${c.rowIndex} c${c.columnIndex}${span}  ${c.alignment}  ` +
          `${c.numeric ? 'numeric' : c.header ? 'header' : 'text'}  font ${c.fontSize}${final !== undefined ? ` → ${final}` : ''} pt  ` +
          `bbox ${box(c.textBox)}  usable ${box(c.usable)}${c.trailingMarker ? `  marker ${c.trailingMarker}` : ''}` +
          `${c.background ? `  bg ${c.background}${c.textOnDark ? ' (white text)' : ''}` : c.maskable ? '' : '  NOT MASKABLE'}` +
          `  ${JSON.stringify(b.text.slice(0, 60))}`,
      );
    }
  }
  tableDiagnosticsList.textContent = lines.join('\n');
  tableDiagnostics.hidden = false;
}

tableDiagnosticsCells.addEventListener('change', () => {
  if (currentLayout) renderTableDiagnostics(currentLayout, lastRenderResult);
});

/**
 * Developer Mode (繁體中文): the generic layout roles — structured abstract
 * regions and their labels, sidebar / callout containers with their inner
 * roles, background-aware masks, and the source-item ownership check.
 */
function renderLayoutRoleDiagnostics(layout: LayoutResult, render: RenderResult | null): void {
  const st = layout.stats;
  const lr = render?.stats.layoutRoles ?? null;
  const regions = layout.structuredRegions;
  const containers = layout.containers;
  const labelBlocks = layout.blocks.filter((b) => roleOf(b) === 'STRUCTURED_LABEL');
  const roleCount = (role: LayoutRole) => layout.blocks.filter((b) => roleOf(b) === role).length;
  const box = (r: { x: number; y: number; width: number; height: number }) => `[${r.x}, ${r.y}, ${r.width}×${r.height}]`;
  const pct = (v: number) => `${Math.round(v * 100)}%`;
  const avg = (values: number[]) => (values.length ? values.reduce((a, b) => a + b, 0) / values.length : 0);
  const claimedByDetector = layout.roleOwnership;

  roleDiagnosticsSummary.textContent =
    `版面角色診斷：結構式摘要 ${st.structuredRegionCount} 區（label ${st.structuredLabelCount} 個）` +
    ` · 側欄 ${st.sidebarCount} · 重點框 ${st.calloutCount}（子區塊 ${st.sidebarChildCount}）` +
    ` · 重複來源項目 ${st.duplicateSourceItems}` +
    (lr ? ` · 容器底色遮罩 ${lr.containerMasks} · 白字 ${lr.lightTextUnits}` : '');

  const lines: string[] = [
    '【Structured Abstract（結構式摘要）】',
    `  區域數：${regions.length}`,
    `  label 數：${st.structuredLabelCount}（區塊角色 STRUCTURED_LABEL ${labelBlocks.length} 個）`,
    `  平均 confidence：區域 ${pct(avg(regions.map((r) => r.confidence)))}，label ${pct(avg(regions.flatMap((r) => r.sections.map((s) => s.confidence))))}`,
    `  section 數：${regions.reduce((n, r) => n + r.sections.length, 0)}`,
    `  門檻：label ≥ ${pct(STRUCTURED_MIN_LABEL_CONFIDENCE)}、區域 ≥ ${pct(STRUCTURED_MIN_REGION_CONFIDENCE)}（低於門檻維持一般 BODY）`,
  ];
  for (const r of regions) {
    lines.push(
      `    ${r.id}  第 ${r.page} 頁  confidence ${pct(r.confidence)}  bbox ${box(r.bbox)}  ${r.sections.length} 個 section  [${r.signals.join(', ')}]`,
    );
    for (const s of r.sections) {
      const label = layout.blocks.find((b) => b.id === s.labelBlock);
      lines.push(
        `      ${s.labelBlock}  ${s.inline ? '同行 run-in' : '獨立一行'}  confidence ${pct(s.confidence)}  正文 ${s.bodyBlocks.length} 塊  ${JSON.stringify(label?.text.slice(0, 40) ?? '')}`,
      );
    }
  }
  lines.push(
    '',
    '【Sidebar / Callout（側欄與重點框）】',
    `  container 數：${containers.length}（側欄 ${st.sidebarCount}、重點框 ${st.calloutCount}）`,
    `  平均 confidence：${pct(avg(containers.map((c) => c.confidence)))}（門檻 ≥ ${pct(SIDEBAR_MIN_CONFIDENCE)}）`,
    `  children 數：${st.sidebarChildCount}`,
    `  heading / label / body：${roleCount('SIDEBAR_HEADING')} / ${roleCount('SIDEBAR_LABEL')} / ${roleCount('SIDEBAR_BODY')}`,
  );
  for (const c of containers) {
    lines.push(
      `    ${c.id}  第 ${c.page} 頁  ${c.type === 'SIDEBAR' ? '側欄' : '重點框'}  bbox ${box(c.bbox)}  背景 ${c.backgroundFill ?? '無（白底）'}${c.textOnDark ? '（深色，白字）' : ''}` +
        `  邊框 ${c.border ? `${c.border.source} ${c.border.thickness} pt` : '無'}  內距 ${c.padding.left}/${c.padding.right}/${c.padding.top}/${c.padding.bottom}` +
        `  confidence ${pct(c.confidence)}  children ${c.children.length}  [${c.signals.join(', ')}]`,
    );
    const kids = c.children.map((id) => layout.blocks.find((b) => b.id === id)).filter((b): b is TextBlock => !!b);
    lines.push(
      `      heading ${kids.filter((b) => roleOf(b) === 'SIDEBAR_HEADING').length} · label ${kids.filter((b) => roleOf(b) === 'SIDEBAR_LABEL').length} · body ${kids.filter((b) => roleOf(b) === 'SIDEBAR_BODY').length}`,
    );
  }
  lines.push(
    '',
    '【Ownership（來源歸屬）】',
    `  claimed source items（側欄 / 結構式摘要角色所擁有的來源文字項目）：${st.roleClaimedSourceItems}`,
    `  duplicate source items：${st.duplicateSourceItems}（必須為 0）`,
    `  各 detector 擁有的區塊：${Object.entries(claimedByDetector).map(([k, v]) => `${k} ${v}`).join('、') || '—'}`,
    `  優先順序：TABLE → FIGURE → SIDEBAR/CALLOUT → STRUCTURED ABSTRACT → CAPTION → BODY`,
  );
  lines.push(
    '',
    '【Typography / 背景遮罩】',
    `  STRUCTURED_LABEL：起始 ${TYPOGRAPHY.structuredLabel.sizeScale}× body，下限 ${TYPOGRAPHY.structuredLabel.minScale}× body，加粗，段前 ${TYPOGRAPHY.spacing.structuredLabelBefore} em，label 後間距 ${TYPOGRAPHY.spacing.structuredLabelAfter} em，section 間距 ${TYPOGRAPHY.spacing.structuredSectionGap} em`,
    `  SIDEBAR_HEADING：起始 ${TYPOGRAPHY.sidebarHeading.sizeScale}× 容器內文，下限 ${TYPOGRAPHY.sidebarHeading.minScale}×；SIDEBAR_LABEL 下限 ${TYPOGRAPHY.sidebarLabel.minScale}× 容器內文`,
    `  最低對比：字級 / 字重 / 間距三者至少 ${TYPOGRAPHY.minimumHeadingBodyContrast.required} 項成立`,
    lr
      ? `  實際：結構式 label ${lr.structuredLabels}、側欄標題 ${lr.sidebarHeadings}、側欄 label ${lr.sidebarLabels}、側欄內文 ${lr.sidebarBodies}；` +
          `同行 label ${lr.inlineLabels}、改為獨立一行 ${lr.ownLineLabels}、對比不足 ${lr.contrastShortfall}`
      : '  實際：—（請先產生 PDF）',
    lr
      ? `  遮罩：容器底色 ${lr.containerMasks} 條、其他填色 ${lr.tintedMasks} 條、深底白字 ${lr.lightTextUnits} 個單元（遮罩只覆蓋字形範圍，不重畫容器與邊框）`
      : '  遮罩：—（請先產生 PDF）',
  );
  if (roleDiagnosticsBlocks.checked) {
    lines.push('', '【角色區塊明細】');
    for (const b of layout.blocks) {
      if (!b.roleDetector || (b.roleDetector !== 'sidebar' && b.roleDetector !== 'structured-abstract')) continue;
      const report = render?.reports.find((r) => r.sourceBlockIds[0] === b.id);
      lines.push(
        `  ${b.id}  p${b.page}  ${ROLE_LABELS_ZH[roleOf(b)]} (${roleOf(b)})  confidence ${pct(b.roleConfidence ?? 1)}  bbox ${box(b)}` +
          (b.containerId ? `  容器 ${b.containerId}` : '') +
          (b.labelFor ? `  label→${b.labelFor}` : '') +
          (report?.background ? `  背景 ${report.background.color ?? '白'}（${report.background.source}${report.background.light ? '，白字' : ''}）` : '') +
          (report?.typography?.labelPlacement ? `  ${report.typography.labelPlacement === 'inline' ? '同行' : '獨立一行'}` : '') +
          (report?.typography?.contrast ? `  對比 ${report.typography.contrast.join('+')}` : '') +
          `  ${JSON.stringify(b.text.slice(0, 50))}`,
      );
    }
  }
  roleDiagnosticsList.textContent = lines.join('\n');
  roleDiagnostics.hidden = false;
}

roleDiagnosticsBlocks.addEventListener('change', () => {
  if (currentLayout) renderLayoutRoleDiagnostics(currentLayout, lastRenderResult);
});

typographyDiagnosticsUnits.addEventListener('change', () => {
  if (currentLayout) renderTypographyDiagnostics(currentLayout, lastRenderResult);
});

function renderResults(analysis: PdfAnalysis, layout: LayoutResult | null): void {
  resFile.textContent = analysis.fileName;
  resPages.textContent = String(analysis.pageCount);
  resItems.textContent = String(analysis.textItemCount);
  resSelectable.textContent = analysis.hasSelectableText ? 'Yes' : 'No';
  resultsEl.hidden = false;

  if (!analysis.hasSelectableText || !layout) {
    resLayout.textContent = '–';
    resBodyFont.textContent = '–';
    resBlocks.textContent = '0';
    resTranslationBlocks.textContent = '0';
    unsupportedEl.hidden = false;
    return;
  }

  if (analysis.suspiciousRatio > 0.2) {
    setText(
      warningEl,
      `${analysis.suspiciousItemCount} text items look unreadable (for example "(cid:123)"). Translation quality will suffer.`,
    );
  }

  resLayout.textContent = describeLayout(layout);
  resBodyFont.textContent = `${layout.bodyFontSize} pt`;
  resBlocks.textContent = String(layout.stats.blockCount);
  resTranslationBlocks.textContent =
    `${layout.stats.translationBlockCount}` +
    ` (${layout.stats.mergedBlockCount} merged across column/page breaks, ${layout.stats.incompleteBlockCount} still incomplete)`;
  const st = layout.stats;
  resContext.textContent =
    `${st.inputChars.toLocaleString()} input chars · ${st.contextChars.toLocaleString()} context chars on ` +
    `${st.contextUnitCount} unit(s) (incomplete / merged / continuation only) · ${st.contextCharsSaved.toLocaleString()} context chars saved vs. sending 300 chars both ways for every unit`;
  debugLimitEl.textContent = String(Math.min(DEBUG_ITEM_LIMIT, analysis.textItemCount));
  toolbar.hidden = false;
  renderTableDiagnostics(layout, null);
  renderTypographyDiagnostics(layout, null);
  renderLayoutRoleDiagnostics(layout, null);

  if (layout.stats.translationBlockCount > 0) {
    testSection.hidden = false;
    fullSection.hidden = false;
    translateBtn.disabled = true; // unlocked by the test checkbox
  }
  updateSelectionUi();
}

// ---------------------------------------------------------------------------
// Selection for Translation Test Mode
// ---------------------------------------------------------------------------

function selectedBlocks(): TranslationBlock[] {
  if (!currentLayout) return [];
  return currentLayout.translationBlocks.filter((b) => selectedIds.has(b.id));
}

function updateSelectionUi(): void {
  const n = selectedIds.size;
  selectionInfo.textContent = `${n} block(s) selected (choose ${TEST_MIN_BLOCKS}–${TEST_MAX_BLOCKS})`;
  testBtn.disabled = isTranslating || n < TEST_MIN_BLOCKS || n > TEST_MAX_BLOCKS;
  testBtn.textContent = `Test Translation (${n})`;
  for (const box of blocksList.querySelectorAll<HTMLInputElement>('input.pick')) {
    box.checked = selectedIds.has(box.value);
  }
}

function toggleSelection(id: string, on: boolean): void {
  if (on) selectedIds.add(id);
  else selectedIds.delete(id);
  updateSelectionUi();
}

/** Pick a representative sample: title, a heading, a caption, a footnote, then body text from different pages. */
function pickSample(): void {
  if (!currentLayout) return;
  const blocks = currentLayout.translationBlocks;
  const picked: string[] = [];
  const takeFirst = (type: TranslationBlock['type']) => {
    const b = blocks.find((x) => x.type === type && !picked.includes(x.id));
    if (b) picked.push(b.id);
  };
  takeFirst('TITLE');
  takeFirst('HEADING');
  takeFirst('CAPTION');
  takeFirst('FOOTNOTE');

  const bodies = blocks.filter((b) => b.type === 'BODY' && b.text.length >= 120);
  const seenPages = new Set<number>();
  for (const b of bodies) {
    if (picked.length >= SAMPLE_SIZE) break;
    if (seenPages.has(b.page)) continue;
    picked.push(b.id);
    seenPages.add(b.page);
  }
  for (const b of bodies) {
    if (picked.length >= SAMPLE_SIZE) break;
    if (!picked.includes(b.id)) picked.push(b.id);
  }

  selectedIds = new Set(picked.slice(0, TEST_MAX_BLOCKS));
  updateSelectionUi();
  if (blocksPanel.hidden) {
    blocksOnlyTranslate.checked = true;
    renderBlockCards(currentLayout);
    blocksPanel.hidden = false;
    blocksToggle.textContent = 'Hide Translation Blocks';
  }
}

pickSampleBtn.addEventListener('click', pickSample);

/**
 * Units of the abstract: from the first block on page 1–2 that starts with
 * "Abstract" up to (not including) the next heading, at most `limit` units.
 * Shared by the translation test and the "Abstract only" render range.
 */
function findAbstractUnits(layout: LayoutResult, limit: number): { ids: string[]; error: string | null; startId: string | null } {
  const blocks = layout.blocks;
  const start = blocks.findIndex((b) => b.page <= 2 && /^abstract\b/i.test(b.text));
  if (start < 0) {
    return { ids: [], error: 'No block starting with "Abstract" was found on pages 1–2. Pick blocks manually.', startId: null };
  }

  const unitBySource = new Map<string, string>();
  for (const unit of layout.translationBlocks) {
    for (const src of unit.sourceBlockIds) unitBySource.set(src, unit.id);
  }

  const picked: string[] = [];
  for (let i = start; i < blocks.length; i++) {
    const b = blocks[i];
    if (i > start && (b.type === 'HEADING' || b.type === 'TITLE')) break;
    if (b.page > blocks[start].page + 1) break;
    if (b.type === 'HEADER' || b.type === 'FOOTER') continue;
    if (!b.translate) continue;
    const unitId = unitBySource.get(b.id);
    if (!unitId) continue;
    if (!picked.includes(unitId)) picked.push(unitId);
    if (picked.length >= limit) break;
  }

  // The "Abstract" line may itself be a heading whose body follows as the next block.
  const startBlock = blocks[start];
  if (picked.length === 0 && startBlock.type === 'HEADING') {
    return { ids: [], error: 'Found the Abstract heading but no translatable body after it.', startId: startBlock.id };
  }
  return { ids: picked, error: null, startId: startBlock.id };
}

function pickAbstract(): void {
  if (!currentLayout) return;
  const { ids: picked, error, startId } = findAbstractUnits(currentLayout, TEST_MAX_BLOCKS);
  if (error) {
    setTestStatus(error);
    return;
  }

  selectedIds = new Set(picked);
  updateSelectionUi();
  setTestStatus(`Selected ${picked.length} unit(s) for the Abstract (from ${startId}).`);
  if (blocksPanel.hidden) {
    blocksOnlyTranslate.checked = true;
    renderBlockCards(currentLayout);
    blocksPanel.hidden = false;
    blocksToggle.textContent = 'Hide Translation Blocks';
  }
}

pickAbstractBtn.addEventListener('click', pickAbstract);
clearSelectionBtn.addEventListener('click', () => {
  selectedIds = new Set();
  updateSelectionUi();
});

// ---------------------------------------------------------------------------
// Translation Test Mode
// ---------------------------------------------------------------------------

type TranslateAllOptions = Omit<DocumentTranslationOptions, 'client' | 'cache' | 'targetLanguage'> & {
  /** Scope fingerprint of this run (part of the terminology cache key); test runs pass their own. */
  scopeKey?: string;
};

/**
 * Shared core: terminology extraction → translation (cache, batches, context,
 * retries, protected entities, post-checks) → QA of high-risk blocks, for any set of units.
 */
async function translateAll(
  blocks: TranslationBlock[],
  into: Map<string, TranslationEntry>,
  options: TranslateAllOptions,
): Promise<TranslationStats> {
  const { scopeKey, ...rest } = options;
  const all = currentLayout?.translationBlocks ?? blocks;
  // Terminology samples only the selected units (plus title / abstract); its per-session
  // cache is keyed by document + scope, and auto terms of earlier scopes of the same
  // document are merged in so every chapter uses the same renderings.
  const documentKey = documentFingerprint(all);
  const result = await translateDocument(blocks, into, {
    client,
    cache,
    targetLanguage: TARGET_LANGUAGE,
    documentOrder: all,
    documentBlocks: unitsInDocumentOrder(all, blocks, terminologyContextUnits(all)),
    terminologyDocumentKey: documentKey,
    terminologyCacheKey: `${documentKey}|${scopeKey ?? 'all'}`,
    ...rest,
  });
  const w = window as unknown as {
    __translations: Record<string, TranslationEntry>;
    __translationStats: TranslationStats;
    __qualityStats: Omit<DocumentTranslationResult, 'translation' | 'terms'>;
    __documentTerminology: DocumentTranslationResult['terms'];
    __jobUsage: ReturnType<JobUsage['snapshot']>;
  };
  // Per-job token accounting: the stage stats already include every retry (client
  // batch retries and the Worker's missing-id retry), and a second run on the same
  // file (Developer Mode test → full run) adds on top instead of replacing.
  jobUsage.add('terminology', result.terminology.usage, result.terminology.durationMs);
  jobUsage.add('translation', result.translation.usage, result.translation.durationMs);
  jobUsage.add('qa', result.qa.usage, result.qa.durationMs);

  w.__translations = Object.fromEntries(into);
  w.__translationStats = result.translation;
  const { translation: _translation, terms, ...quality } = result;
  w.__qualityStats = quality;
  w.__documentTerminology = terms;
  w.__jobUsage = jobUsage.snapshot();
  renderCostStats(result);
  renderTerminologyPanel(result);
  renderQaPanel(result, blocks, into);
  return result.translation;
}

/** Developer Mode: the document glossary (user entries first, then automatic ones). */
function renderTerminologyPanel(r: DocumentTranslationResult): void {
  const t = r.terminology;
  terminologySummary.textContent =
    `Terminology: ${t.autoTerms} auto term(s), ${t.userTerms} user term(s)` +
    (t.cached ? ' (auto terms reused from this session)' : t.requests ? ` (1 extraction request, ${t.samples} excerpts / ${t.sampleChars.toLocaleString()} chars)` : '');
  const lines = r.terms.map((e) => `${e.source} → ${e.target}${e.abbreviation ? ` (${e.abbreviation})` : ''}  [${e.origin}]`);
  if (t.warnings.length) lines.push('', ...t.warnings.map((w) => `warning: ${w}`));
  terminologyList.textContent = lines.join('\n') || 'none';
  terminologyPanel.hidden = false;
}

/** Developer Mode: high-risk blocks, QA results and the automatic warnings. */
function renderQaPanel(r: DocumentTranslationResult, blocks: TranslationBlock[], into: Map<string, TranslationEntry>): void {
  const s = r.translation;
  const q = r.qa;
  const translated = s.translatedBlocks + s.cachedBlocks;
  const p = r.qaPolicy;
  const pct = translated > 0 ? Math.round((q.blocksSent / translated) * 100) : 0;
  qaSummary.textContent = `Quality Assurance: ${r.highRiskBlocks} hard-risk · ${r.softRiskBlocks} soft-risk · ${q.blocksChecked} QA checked · ${q.correctedBlocks} corrected (${pct}% of blocks sent to QA)`;
  const lines = [
    `hard-risk blocks:       ${r.highRiskBlocks} of ${translated} translated (QA candidates)`,
    `soft-risk blocks:       ${r.softRiskBlocks} (weighting signals only, not sent to QA)`,
    `QA checked:             ${q.blocksChecked} (sent ${q.blocksSent}, ${q.requests} request(s), ${q.failedBlocks} unanswered)`,
    `QA corrected:           ${q.correctedBlocks}${q.rejectedCorrections ? ` (${q.rejectedCorrections} correction(s) rejected by the sanity check)` : ''}`,
    `correction rate:        ${correctionRate(q)}`,
    '',
    'QA avoided (policy):',
    `  soft-risk skipped by policy:  ${p.softRiskSkipped}`,
    `  hard-risk checked:            ${p.hardRiskChecked}`,
    `  critical mismatch forced QA:  ${p.criticalForced}`,
    `  budget-skipped hard-risk:     ${p.budgetSkippedHardRisk}  (budget ${p.budget} block(s))`,
    '',
    `numeric warnings:       ${s.numericWarnings}`,
    `citation warnings:      ${s.citationWarnings}`,
    `placeholder warnings:   ${s.placeholderWarnings}  (protected entities: ${s.protectedEntities})`,
  ];
  for (const w of r.warnings) lines.push(`warning: ${w}`);
  const flagged = blocks.filter((b) => (into.get(b.id)?.quality?.riskLevel ?? 'none') !== 'none');
  if (flagged.length) {
    lines.push('', 'risk-flagged blocks (hard = QA candidate, soft = reported only):');
    for (const b of flagged) {
      const e = into.get(b.id);
      const qm = e?.quality;
      if (!qm) continue;
      const detail: string[] = [];
      if (qm.numericMissing.length) detail.push(`numbers missing ${qm.numericMissing.join(' ')}`);
      if (qm.numericAdded.length) detail.push(`numbers added ${qm.numericAdded.join(' ')}`);
      if (qm.citationMissing.length) detail.push(`citations missing ${qm.citationMissing.join(' ')}`);
      if (qm.placeholderMissing.length) detail.push(`placeholders lost ${qm.placeholderMissing.join(' ')}`);
      const triggers = qm.qaTriggers.length ? `  triggers [${qm.qaTriggers.join(', ')}]` : '';
      lines.push(
        `  ${b.id}  p.${b.page}  ${qm.riskLevel}  score ${qm.riskScore}${triggers}  signals [${qm.riskReasons.join(', ')}]  QA: ${qm.qa}${qm.qaIssues.length ? ` (${qm.qaIssues.join(', ')})` : ''}${detail.length ? `  ${detail.join('; ')}` : ''}`,
      );
    }
  }
  qaList.textContent = lines.join('\n');
  qaPanel.hidden = false;
}

function usageSummary(u: WorkerUsage | null | undefined): string {
  if (!u) return 'usage not reported';
  return `input ${u.inputTokens.toLocaleString()} · output ${u.outputTokens.toLocaleString()} · total ${(u.inputTokens + u.outputTokens).toLocaleString()}`;
}

function sumUsage(parts: (WorkerUsage | null | undefined)[]): WorkerUsage | null {
  let sum: WorkerUsage | null = null;
  for (const p of parts) {
    if (!p) continue;
    const acc: WorkerUsage = sum ?? { inputTokens: 0, outputTokens: 0, cachedInputTokens: 0, reasoningTokens: 0 };
    acc.inputTokens += p.inputTokens;
    acc.outputTokens += p.outputTokens;
    acc.cachedInputTokens += p.cachedInputTokens;
    acc.reasoningTokens += p.reasoningTokens;
    sum = acc;
  }
  return sum;
}

function correctionRate(q: { blocksChecked: number; correctedBlocks: number }): string {
  return q.blocksChecked > 0 ? `${((q.correctedBlocks / q.blocksChecked) * 100).toFixed(1)}%` : 'n/a';
}

/** Developer Mode: requests, chars and tokens of the last translation run. */
function renderCostStats(r: DocumentTranslationResult): void {
  const s = r.translation;
  const u = s.usage;
  const currentInput = u ? u.inputTokens : s.estimatedInputTokens;
  const saved = s.baselineEstimatedInputTokens - currentInput;
  const pct = s.baselineEstimatedInputTokens > 0 ? Math.round((saved / s.baselineEstimatedInputTokens) * 100) : 0;
  const lines = [
    `API requests:            ${s.requests}  (provider calls ${s.providerCalls}, retry requests ${s.retryRequests})`,
    `translated blocks:       ${s.translatedBlocks}`,
    `cache hits:              ${s.cachedBlocks}`,
    `skipped blocks (no API): ${s.skippedBlocks}`,
    `failed blocks:           ${s.failedBlocks}`,
    `input chars:             ${s.inputChars.toLocaleString()}`,
    `context chars:           ${s.contextChars.toLocaleString()} sent on ${s.extraContextBlocks} block(s)  (assigned ${s.contextCharsAssigned.toLocaleString()}, dropped inside batches ${(s.contextCharsAssigned - s.contextChars).toLocaleString()})`,
    `estimated input tokens:  ${s.estimatedInputTokens.toLocaleString()}`,
    `estimated output tokens: ${s.estimatedOutputTokens.toLocaleString()}`,
    `total estimated tokens:  ${(s.estimatedInputTokens + s.estimatedOutputTokens).toLocaleString()}`,
    `retry tokens:            ${s.retryTokens.toLocaleString()} (${u ? 'API usage' : 'estimated'})`,
    u
      ? `API usage:               input ${u.inputTokens.toLocaleString()} (cached ${u.cachedInputTokens.toLocaleString()}) · output ${u.outputTokens.toLocaleString()} · total ${(u.inputTokens + u.outputTokens).toLocaleString()}`
      : 'API usage:               not reported by the Worker',
    '',
    `Before optimization estimate: ${s.baselineEstimatedInputTokens.toLocaleString()} input tokens (${s.baselineRequests} requests, 15 blocks / 12k chars, full prompt, 150-char contexts)`,
    `Current:                      ${currentInput.toLocaleString()} input tokens (${u ? 'API usage' : 'estimate'}, ${s.requests} requests)`,
    `Saved:                        ${saved.toLocaleString()} tokens (${pct}%)`,
    `translation time:             ${(s.durationMs / 1000).toFixed(1)}s`,
    '',
    'Cost breakdown (API usage):',
    `  First-pass:   ${s.requests} request(s) · ${usageSummary(u)}`,
    `  Terminology:  ${r.terminology.requests} request(s)${r.terminology.cached ? ' (reused from session cache)' : ''} · ${usageSummary(r.terminology.usage)}`,
    `  QA:           ${r.qa.requests} request(s) · ${usageSummary(r.qa.usage)}`,
    `  Total:        ${s.requests + r.terminology.requests + r.qa.requests} request(s) · ${usageSummary(sumUsage([u, r.terminology.usage, r.qa.usage]))}`,
    '',
    'QA efficiency:',
    `  QA checked:       ${r.qa.blocksChecked} of ${s.translatedBlocks} translated (${s.translatedBlocks ? Math.round((r.qa.blocksSent / s.translatedBlocks) * 100) : 0}% sent)`,
    `  QA corrected:     ${r.qa.correctedBlocks}`,
    `  Correction rate:  ${correctionRate(r.qa)}`,
    `  QA time:          ${(r.qa.durationMs / 1000).toFixed(1)}s · terminology time: ${(r.terminology.durationMs / 1000).toFixed(1)}s`,
    '',
    '--- Token Usage Summary (this job, actual provider usage) ---',
    ...formatUsageBreakdown(jobUsage.snapshot()),
    '',
    '--- Translation Scope (this run) ---',
    ...scopeRunLines(r),
  ];
  costStatsList.textContent = lines.join('\n');
  costStats.hidden = false;
}

async function runTest(): Promise<void> {
  if (!currentLayout || isTranslating) return;
  const blocks = selectedBlocks();
  if (blocks.length < TEST_MIN_BLOCKS || blocks.length > TEST_MAX_BLOCKS) return;
  const terminology = currentTerminology();
  if (terminology === null) return;

  isTranslating = true;
  updateSelectionUi();
  testOkWrapper.hidden = true;
  testOkCheckbox.checked = false;
  translateBtn.disabled = true;
  setError(null);

  renderTestPanel(blocks);
  setTestStatus(`Translating ${blocks.length} test block(s) with ${providerLabel}...`);
  const started = performance.now();

  try {
    await translateAll(blocks, entries, {
      userTerminology: terminology,
      scopeKey: `test:${fnv1a([...selectedIds].sort().join(','))}`,
      maxBlocksPerBatch: TEST_MAX_BLOCKS,
      concurrency: 1,
      onProgress: (p) => setTestStatus(`${p.message}  (${p.blocksDone} / ${p.blocksTotal})`),
      onEntry: (entry) => {
        updateTestEntry(entry);
        updateBlockCardEntry(entry);
      },
    });
  } finally {
    isTranslating = false;
    updateSelectionUi();
    const seconds = ((performance.now() - started) / 1000).toFixed(1);
    const failed = blocks.filter((b) => entries.get(b.id)?.status === 'failed').length;
    if (failed === 0) {
      setTestStatus(`Test finished in ${seconds}s. Review the translations below, then confirm to unlock the full translation.`);
      testOkWrapper.hidden = false;
    } else {
      setTestStatus(`Test finished in ${seconds}s with ${failed} failed block(s). Fix the Worker/provider before running the full PDF.`);
    }
    console.log('[Translation Test]', blocks.map((b) => ({ id: b.id, entry: entries.get(b.id) })));
    updateExportUi();
  }
}

testBtn.addEventListener('click', () => void runTest());

testOkCheckbox.addEventListener('change', () => {
  translateBtn.disabled = !testOkCheckbox.checked || isTranslating;
});

function renderTestPanel(blocks: TranslationBlock[]): void {
  const fragment = document.createDocumentFragment();
  for (const block of blocks) {
    const card = el('article', 'preview-item');
    card.dataset.id = block.id;
    const head = el('div', 'preview-head');
    head.appendChild(el('span', 'tag', `Page ${block.page}`));
    head.appendChild(el('span', `tag tag-type tag-${block.type.toLowerCase()}`, block.type));
    head.appendChild(el('span', 'tag tag-id', block.id));
    head.appendChild(el('span', 'tag tag-status', entries.get(block.id)?.status ?? 'pending'));
    card.appendChild(head);
    appendUnitMeta(card, block, null);
    card.appendChild(el('div', 'label', 'Original:'));
    card.appendChild(el('p', 'original', block.text));
    card.appendChild(el('div', 'label', translationLabel()));
    card.appendChild(el('p', 'translation zh muted', 'Waiting…'));
    fragment.appendChild(card);
  }
  testList.replaceChildren(fragment);
}

function applyEntryToCard(card: HTMLElement | null, entry: TranslationEntry): void {
  if (!card) return;
  const status = card.querySelector<HTMLElement>('.tag-status');
  const translation = card.querySelector<HTMLElement>('.translation');
  if (status) {
    status.textContent = entry.status;
    status.dataset.status = entry.status;
  }
  applyQualityTag(card, entry);
  if (!translation) return;
  if (entry.status === 'done' || entry.status === 'cached') {
    translation.textContent = entry.translation ?? '';
    translation.classList.remove('muted', 'error-text');
  } else if (entry.status === 'failed') {
    translation.textContent = `Failed: ${entry.error ?? 'unknown error'}`;
    translation.classList.remove('muted');
    translation.classList.add('error-text');
  } else {
    translation.textContent = entry.status === 'translating' ? 'Translating…' : 'Waiting…';
    translation.classList.add('muted');
  }
}

/** Risk / QA tag next to the status tag (Developer Mode cards). */
function applyQualityTag(card: HTMLElement, entry: TranslationEntry): void {
  const head = card.querySelector<HTMLElement>('.preview-head');
  if (!head) return;
  let tag = head.querySelector<HTMLElement>('.tag-qa');
  const q = entry.quality;
  if (!q || q.riskLevel === 'none') {
    tag?.remove();
    return;
  }
  if (!tag) {
    tag = el('span', 'tag tag-qa');
    head.appendChild(tag);
  }
  tag.textContent =
    q.riskLevel === 'hard'
      ? `hard-risk ${q.riskScore} [${q.qaTriggers.join(', ')}] · QA ${q.qa}${q.qaIssues.length ? `: ${q.qaIssues.join(', ')}` : ''}`
      : `soft-risk ${q.riskScore}`;
  tag.title = q.riskReasons.join(', ');
}

function updateTestEntry(entry: TranslationEntry): void {
  applyEntryToCard(testList.querySelector<HTMLElement>(`[data-id="${entry.id}"]`), entry);
}

// ---------------------------------------------------------------------------
// Full translation (unlocked after the test)
// ---------------------------------------------------------------------------

async function runTranslation(blocks: TranslationBlock[], scopeKey: string, retry = false): Promise<void> {
  if (!currentLayout || isTranslating || blocks.length === 0) return;
  if (!testOkCheckbox.checked) {
    setError('Run a translation test and confirm the quality before translating the full PDF.');
    return;
  }
  const terminology = currentTerminology();
  if (terminology === null) return;
  if (!lastScope?.ok) return;
  // Same lifecycle as User Mode: confirm the shown quote → payment → verification → gate.
  // A retry of a failed run (Retry Failed) finds the confirmed quote and the payment already there.
  const context = jobContextFor(lastScope);
  if (!(await ensureQuoteConfirmedAndPaid(lastScope))) return;
  const start = lifecycle.startTranslation(context);
  if (!start.ok) {
    setError(gateFailureMessage(start.gate));
    return;
  }
  if (!retry) startJob(); // one job (token counters) per scope run

  isTranslating = true;
  translateBtn.disabled = true;
  retryBtn.hidden = true;
  updateSelectionUi();
  setError(null);

  previewEl.hidden = false;
  renderPreview(currentLayout);

  let failed = 0;
  try {
    const stats = await translateAll(blocks, entries, {
      userTerminology: terminology,
      scopeKey,
      onProgress: (p) => {
        setTranslateStatus(`${p.message}  (${p.blocksDone} / ${p.blocksTotal} blocks${p.blocksFailed ? `, ${p.blocksFailed} failed` : ''})`);
      },
      onEntry: (entry) => {
        updatePreviewEntry(entry);
        updateBlockCardEntry(entry);
      },
    });
    // The validation compares the run with the quote the user confirmed, never with a fresh buildQuote().
    recordPricingValidation(start.confirmed.snapshot, stats, !start.retry);
  } finally {
    isTranslating = false;
    translateBtn.disabled = !testOkCheckbox.checked;
    updateSelectionUi();
    failed = [...entries.values()].filter((e) => e.status === 'failed').length;
    if (failed > 0) lifecycle.fail(`${failed} block(s) failed`); // keeps the confirmed quote + payment for Retry Failed
    else lifecycle.complete();
    retryBtn.hidden = failed === 0;
    if (failed > 0) retryBtn.textContent = `Retry Failed (${failed})`;
    renderPreviewSummary();
    console.log('[Translation]', Object.fromEntries(entries));
    updateExportUi();
  }
}

translateBtn.addEventListener('click', () => {
  const scope = resolveCurrentScope();
  if (!scope) return;
  if (!scope.ok) {
    setError(scope.error);
    return;
  }
  lastScope = scope;
  renderScopeDevPanel(scope);
  void runTranslation(scope.units, scope.fingerprint);
});

retryBtn.addEventListener('click', () => {
  if (!currentLayout) return;
  const failedIds = new Set([...entries.values()].filter((e) => e.status === 'failed').map((e) => e.id));
  const units = lastScope?.ok ? lastScope.units : currentLayout.translationBlocks;
  void runTranslation(
    units.filter((b) => failedIds.has(b.id)),
    lastScope?.fingerprint ?? 'all',
    true,
  );
});

// ---------------------------------------------------------------------------
// Rendering: translation preview
// ---------------------------------------------------------------------------

function renderPreview(layout: LayoutResult): void {
  const fragment = document.createDocumentFragment();
  for (const block of layout.translationBlocks) {
    const card = el('article', 'preview-item');
    card.dataset.id = block.id;

    const head = el('div', 'preview-head');
    head.appendChild(el('span', 'tag', `Page ${block.page}`));
    head.appendChild(el('span', `tag tag-type tag-${block.type.toLowerCase()}`, block.type));
    head.appendChild(el('span', 'tag tag-id', block.id));
    head.appendChild(el('span', 'tag tag-status', entries.get(block.id)?.status ?? 'pending'));
    card.appendChild(head);
    appendUnitMeta(card, block, null);

    card.appendChild(el('div', 'label', 'Original:'));
    card.appendChild(el('p', 'original', block.text));
    card.appendChild(el('div', 'label', translationLabel()));
    card.appendChild(el('p', 'translation zh muted', 'Waiting…'));
    fragment.appendChild(card);
  }
  previewList.replaceChildren(fragment);
  for (const entry of entries.values()) updatePreviewEntry(entry);
  renderPreviewSummary();
}

function updatePreviewEntry(entry: TranslationEntry): void {
  applyEntryToCard(previewList.querySelector<HTMLElement>(`[data-id="${entry.id}"]`), entry);
}

function renderPreviewSummary(): void {
  if (!currentLayout) return;
  const total = currentLayout.translationBlocks.length;
  let done = 0;
  let failed = 0;
  let skipped = 0;
  for (const e of entries.values()) {
    if (e.status === 'done' || e.status === 'cached') done++;
    else if (e.status === 'failed') failed++;
    else if (e.status === 'skipped') skipped++;
  }
  previewSummary.textContent =
    `${done} / ${total} blocks translated${failed ? `, ${failed} failed` : ''}${skipped ? `, ${skipped} kept as is (no API call)` : ''}. Cache entries: ${cache.size}.`;
}

// ---------------------------------------------------------------------------
// Rendering: translation blocks (debug + selection)
// ---------------------------------------------------------------------------

/**
 * One card per translation unit (a merged unit shows all its source blocks),
 * plus one card per skipped layout block when "only translatable" is off.
 */
function renderBlockCards(layout: LayoutResult): void {
  const onlyTranslate = blocksOnlyTranslate.checked;
  const unitBySource = new Map<string, TranslationBlock>();
  for (const unit of layout.translationBlocks) {
    for (const src of unit.sourceBlockIds) unitBySource.set(src, unit);
  }
  const blockById = new Map(layout.blocks.map((b) => [b.id, b]));
  const rendered = new Set<string>();
  const fragment = document.createDocumentFragment();

  for (const block of layout.blocks) {
    const unit = unitBySource.get(block.id);
    if (unit) {
      if (rendered.has(unit.id)) continue;
      rendered.add(unit.id);
      const sources = unit.sourceBlockIds.map((id) => blockById.get(id)).filter((b): b is TextBlock => !!b);
      fragment.appendChild(makeUnitCard(unit, sources));
    } else if (!onlyTranslate) {
      fragment.appendChild(makeSkippedCard(block));
    }
  }
  blocksList.replaceChildren(fragment);
  for (const entry of entries.values()) updateBlockCardEntry(entry);
  updateSelectionUi();
}

function geometryLine(block: TextBlock): string {
  return (
    `${block.id}  page ${block.page}  ${block.column}  x: ${block.x}  y: ${block.y}  width: ${block.width}  height: ${block.height}  top: ${block.top}\n` +
    `    fontSize: ${block.fontSize}  lines: ${block.lineCount}  font: ${block.fontRealName ?? block.fontName}\n` +
    `    sectionType=${block.sectionType}  blockType=${block.blockType}  translationEligible=${block.translate}  skipReason=${block.skipReason ?? '—'}`
  );
}

/** Tags + collapsible details shared by the block, test and preview cards. */
function appendUnitMeta(card: HTMLElement, unit: TranslationBlock, sources: TextBlock[] | null): void {
  const head = card.querySelector('.preview-head');
  if (head) {
    if (unit.wasMerged) head.appendChild(el('span', 'tag tag-merged', `merged ×${unit.sourceBlockIds.length}`));
    if (unit.incompleteSource) head.appendChild(el('span', 'tag tag-incomplete', 'incomplete source'));
    if (unit.pages.length > 1) head.appendChild(el('span', 'tag', `pages ${unit.pages.join('–')}`));
    if (unit.overlayEligible === true) head.appendChild(el('span', 'tag tag-overlay', 'overlay'));
    else if (unit.overlayEligible === false) head.appendChild(el('span', 'tag tag-no-overlay', `no overlay: ${unit.overlaySkippedReason}`));
    if (unit.translationEligible === false) head.appendChild(el('span', 'tag tag-no-overlay', `not translated: ${unit.translationSkippedReason}`));
  }

  const details = el('details', 'unit-details');
  details.appendChild(el('summary', undefined, `sourceBlockIds: ${unit.sourceBlockIds.join(', ')}`));
  const lines: string[] = [
    `sectionType:      ${unit.sectionType}`,
    `blockType:        ${unit.blockType}`,
    `translationEligible: ${unit.translationEligible ?? true}${unit.translationSkippedReason ? ` (${unit.translationSkippedReason})` : ''}`,
    `wasMerged:        ${unit.wasMerged}`,
    `incompleteSource: ${unit.incompleteSource}`,
    `mergeReason:      ${unit.mergeReason ?? '—'}`,
    `contextReason:    ${unit.contextReason ?? '— (no extra context sent)'}`,
    `overlayEligible:  ${unit.overlayEligible ?? '—'}`,
    `overlaySkippedReason: ${unit.overlaySkippedReason ?? '—'}`,
  ];
  if (sources) {
    lines.push('', 'source blocks:');
    for (const s of sources) lines.push(geometryLine(s));
  }
  lines.push('', `previousContext:  ${unit.previousContext ?? '—'}`, '', `nextContext:      ${unit.nextContext ?? '—'}`);
  details.appendChild(el('pre', 'block-geom', lines.join('\n')));
  card.appendChild(details);
}

function makeUnitCard(unit: TranslationBlock, sources: TextBlock[]): HTMLElement {
  const card = el('article', 'block-item');
  card.dataset.id = unit.id;

  const head = el('div', 'preview-head');
  const pick = el('label', 'pick-label');
  const box = el('input', 'pick') as HTMLInputElement;
  box.type = 'checkbox';
  box.value = unit.id;
  box.checked = selectedIds.has(unit.id);
  box.addEventListener('change', () => toggleSelection(unit.id, box.checked));
  pick.appendChild(box);
  pick.appendChild(document.createTextNode(' test'));
  head.appendChild(pick);
  head.appendChild(el('span', 'tag tag-id', unit.id));
  head.appendChild(el('span', 'tag', `Page ${unit.page}`));
  head.appendChild(el('span', `tag tag-type tag-${unit.type.toLowerCase()}`, unit.type));
  if (unit.blockType !== unit.type) head.appendChild(el('span', 'tag', unit.blockType));
  if (unit.sectionType !== 'MAIN') head.appendChild(el('span', 'tag', unit.sectionType));
  head.appendChild(el('span', 'tag', sources[0]?.column ?? ''));
  head.appendChild(el('span', 'tag tag-yes', 'translate'));
  card.appendChild(head);

  appendUnitMeta(card, unit, sources);
  card.appendChild(el('div', 'label', 'Original:'));
  card.appendChild(el('p', 'original', unit.text));
  card.appendChild(el('div', 'label', translationLabel()));
  card.appendChild(el('p', 'translation zh muted', '—'));
  return card;
}

function makeSkippedCard(block: TextBlock): HTMLElement {
  const card = el('article', 'block-item is-skipped');
  card.dataset.id = block.id;
  const head = el('div', 'preview-head');
  head.appendChild(el('span', 'tag tag-id', block.id));
  head.appendChild(el('span', 'tag', `Page ${block.page}`));
  head.appendChild(el('span', `tag tag-type tag-${block.type.toLowerCase()}`, block.type));
  if (block.blockType !== block.type) head.appendChild(el('span', 'tag', block.blockType));
  if (block.sectionType !== 'MAIN') head.appendChild(el('span', 'tag', block.sectionType));
  head.appendChild(el('span', 'tag', block.column));
  head.appendChild(el('span', 'tag tag-no', `skip: ${block.skipReason}`));
  card.appendChild(head);
  card.appendChild(el('pre', 'block-geom', geometryLine(block)));
  card.appendChild(el('div', 'label', 'Original:'));
  card.appendChild(el('p', 'original', block.text));
  return card;
}

function updateBlockCardEntry(entry: TranslationEntry): void {
  const card = blocksList.querySelector<HTMLElement>(`[data-id="${entry.id}"]`);
  const translation = card?.querySelector<HTMLElement>('.translation');
  if (!card || !translation) return;
  applyQualityTag(card, entry);
  if (entry.status === 'done' || entry.status === 'cached') {
    translation.textContent = entry.translation ?? '';
    translation.classList.remove('muted', 'error-text');
  } else if (entry.status === 'failed') {
    translation.textContent = `Failed: ${entry.error ?? 'unknown error'}`;
    translation.classList.remove('muted');
    translation.classList.add('error-text');
  } else {
    translation.textContent = entry.status;
    translation.classList.add('muted');
  }
}

blocksToggle.addEventListener('click', () => {
  if (!currentLayout) return;
  const willShow = blocksPanel.hidden;
  if (willShow) renderBlockCards(currentLayout);
  blocksPanel.hidden = !willShow;
  blocksToggle.textContent = willShow ? 'Hide Translation Blocks' : 'Show Translation Blocks';
});

blocksOnlyTranslate.addEventListener('change', () => {
  if (currentLayout && !blocksPanel.hidden) renderBlockCards(currentLayout);
});

// ---------------------------------------------------------------------------
// Rendering: raw text items (Phase B debug)
// ---------------------------------------------------------------------------

function renderDebugItems(items: TextItemDebug[]): void {
  const fragment = document.createDocumentFragment();
  items.slice(0, DEBUG_ITEM_LIMIT).forEach((item, index) => {
    const card = el('article', 'debug-item');
    card.appendChild(el('h3', undefined, `#${index + 1}  Page ${item.page}`));
    card.appendChild(
      el(
        'pre',
        undefined,
        [
          `Text:      ${JSON.stringify(item.text)}`,
          `x:         ${item.x}`,
          `y:         ${item.y}`,
          `width:     ${item.width}`,
          `height:    ${item.height}`,
          `fontSize:  ${item.fontSize}`,
          `fontName:  ${item.fontName}${item.fontRealName ? ` (${item.fontRealName})` : item.fontFamily ? ` (${item.fontFamily})` : ''}`,
          `hasEOL:    ${item.hasEOL}`,
          `transform: [${item.transform.join(', ')}]`,
        ].join('\n'),
      ),
    );
    fragment.appendChild(card);
  });
  debugList.replaceChildren(fragment);
}

debugToggle.addEventListener('click', () => {
  if (!currentAnalysis) return;
  const willShow = debugPanel.hidden;
  if (willShow && debugList.childElementCount === 0) renderDebugItems(currentAnalysis.items);
  debugPanel.hidden = !willShow;
  debugToggle.textContent = willShow ? 'Hide Debug Data' : 'Show Debug Data';
});

// ---------------------------------------------------------------------------
// Phase D–F: PDF export (pdf-lib overlay)
// ---------------------------------------------------------------------------

type RenderRange = 'page1' | 'first3' | 'abstract' | 'full';

function translatedUnitCount(): number {
  if (!currentLayout) return 0;
  let n = 0;
  for (const unit of currentLayout.translationBlocks) {
    const e = entries.get(unit.id);
    if (e && (e.status === 'done' || e.status === 'cached') && e.translation) n++;
  }
  return n;
}

function resetExport(): void {
  if (generatedUrl) {
    URL.revokeObjectURL(generatedUrl);
    generatedUrl = null;
  }
  exportSection.hidden = true;
  downloadLink.hidden = true;
  downloadLink.removeAttribute('href');
  renderProgress.hidden = true;
  renderProgress.value = 0;
  renderWarnings.hidden = true;
  renderWarningsList.textContent = '';
  setText(renderStatus, null);
  setText(renderError, null);
  generateBtn.disabled = true;
  (window as unknown as { __pdfRender?: unknown }).__pdfRender = undefined;
}

/** Enable the button when there is something to render; explain what will happen. */
function updateExportUi(): void {
  if (!currentLayout || !currentAnalysis) {
    generateBtn.disabled = true;
    return;
  }
  const units = currentLayout.translationBlocks;
  const eligible = units.filter((u) => u.overlayEligible).length;
  const translated = translatedUnitCount();
  const debug = renderDebug.checked;
  const bilingual = currentExportMode() === 'bilingual';
  generateBtn.textContent = debug
    ? `Generate Debug PDF (${bilingual ? 'spreads with boxes' : 'bounding boxes'})`
    : bilingual
      ? 'Generate Bilingual PDF'
      : 'Generate Translated PDF';
  generateBtn.disabled = isGenerating || (!debug && translated === 0);
  exportSummary.textContent =
    `${translated} / ${units.length} units translated · ${eligible} units eligible for overlay ` +
    `(TITLE / HEADING / BODY / CAPTION / FOOTNOTE / TABLE / FIGURE without image overlap) · fonts: ${FONT_SOURCES.cjk.label} for Chinese, ` +
    `${FONT_SOURCES.latin.label} for Latin/digits, ${FONT_SOURCES.symbol.label} for symbols, fallback ${FONT_SOURCES.fallback.label}` +
    (bilingual
      ? ' · each output page = [ original page | translated page ], same height, double width'
      : ' · original pages are rewritten in place') +
    (debug
      ? ' · debug mode draws boxes only, nothing is masked'
      : translated === 0
        ? ' · run a translation test or the full translation first'
        : '');
}

function currentExportMode(): RenderOutput {
  return exportMode.value === 'translated' ? 'translated' : 'bilingual';
}

interface RenderRangeSelection {
  pages: Set<number> | null;
  unitIds: Set<string> | null;
  label: string;
  /** File-name suffix for the exported range ("", "_p17", "_Methods_p5-8"). */
  suffix: string;
}

function rangeToPages(range: RenderRange): RenderRangeSelection {
  if (!currentLayout || !currentAnalysis) return { pages: new Set([1]), unitIds: null, label: 'page 1', suffix: '' };
  const pageCount = currentAnalysis.pageCount;
  switch (range) {
    case 'page1':
      return { pages: new Set([1]), unitIds: null, label: 'page 1', suffix: '' };
    case 'first3':
      return {
        pages: new Set([1, 2, 3].filter((p) => p <= pageCount)),
        unitIds: null,
        label: `first ${Math.min(3, pageCount)} pages`,
        suffix: '',
      };
    case 'abstract': {
      const { ids } = findAbstractUnits(currentLayout, 50);
      const unitById = new Map(currentLayout.translationBlocks.map((u) => [u.id, u]));
      const pages = new Set<number>();
      for (const id of ids) for (const p of unitById.get(id)?.pages ?? []) pages.add(p);
      return { pages: pages.size ? pages : new Set([1]), unitIds: new Set(ids), label: `abstract (${ids.length} units)`, suffix: '' };
    }
    default: {
      // Same export rule as User Mode: the scope's pages, or the whole document
      // for 整份論文 / 保留完整 PDF. The overlay is always the last translated scope only.
      const scope: TranslationScope = lastScope?.ok ? lastScope.scope : { mode: 'all' };
      const output = resolveOutputPages(scope, chapterDetection?.chapters ?? [], pageCount, { keepFullDocument: scopeFull.checked });
      return {
        pages: output.pages ? new Set(output.pages) : null,
        unitIds: scopeUnitIds(),
        label: output.full ? `all ${pageCount} pages` : `${output.pages?.length ?? 0} page(s) — ${output.label}`,
        suffix: output.fileSuffix,
      };
    }
  }
}

/** Unit ids of the last translated scope, null for the whole document (= no filter). */
function scopeUnitIds(): Set<string> | null {
  if (!lastScope || !lastScope.ok || lastScope.scope.mode === 'all') return null;
  return new Set(lastScope.units.map((u) => u.id));
}

function showRenderWarnings(result: RenderResult): void {
  const skipped = result.reports.filter((r) => r.skipped);
  const lines: string[] = [];
  for (const w of result.warnings) {
    lines.push(
      `${w.unitId}  page ${w.page}  ${w.type}  reason=${w.reason}  fontSize ${w.fontSize} → ${w.finalFontSize}  lines=${w.lines}` +
        (w.message ? `\n    ${w.message}` : ''),
    );
  }
  if (skipped.length) {
    lines.push('', `skipped units (${skipped.length}):`);
    const byReason = new Map<string, string[]>();
    for (const s of skipped) {
      const key = s.reason ?? '?';
      const list = byReason.get(key) ?? [];
      list.push(s.unitId);
      byReason.set(key, list);
    }
    for (const [reason, ids] of byReason) {
      lines.push(`  ${reason}: ${ids.slice(0, 20).join(', ')}${ids.length > 20 ? ` … (+${ids.length - 20})` : ''}`);
    }
  }
  renderWarningsSummary.textContent = `${result.warnings.length} layout warning(s), ${skipped.length} skipped unit(s)`;
  renderWarningsList.textContent = lines.join('\n') || 'none';
  renderWarnings.hidden = false;
}

interface BuildPdfOptions {
  pdf: AnalyzedPdf & { layout: LayoutResult };
  fileName: string;
  entries: Map<string, TranslationEntry>;
  mode: RenderMode;
  output: RenderOutput;
  pages: Set<number> | null;
  unitIds: Set<string> | null;
  onStatus: (message: string) => void;
  onProgress: (p: RenderProgress) => void;
}

interface BuiltPdf {
  result: RenderResult;
  /** Object URL of the PDF Blob; the caller owns (and revokes) it. */
  url: string;
  size: number;
  fontNotes: string[];
}

/** Shared core: load fonts, run the pdf-lib overlay / side-by-side export, wrap the bytes in a Blob URL. */
async function buildPdf(o: BuildPdfOptions): Promise<BuiltPdf> {
  let fonts: FontSetBytes | null = null;
  if (o.mode === 'overlay') {
    o.onStatus('Loading fonts...');
    fonts = await loadFontSet(o.onStatus);
  }
  o.onStatus('Preparing PDF...');
  const result = await generateTranslatedPdf({
    pdfBytes: o.pdf.buffer,
    fileName: o.fileName,
    analysis: o.pdf.analysis,
    layout: o.pdf.layout,
    entries: o.entries,
    mode: o.mode,
    output: o.output,
    pages: o.pages,
    unitIds: o.unitIds,
    fonts,
    debugRoles: roleDiagnosticsOverlay.checked,
    onProgress: o.onProgress,
  });
  // The Blob copies the bytes; the Uint8Array is dropped with `result`.
  const blob = new Blob([result.bytes as BlobPart], { type: 'application/pdf' });
  (window as unknown as { __pdfRender: unknown }).__pdfRender = { reports: result.reports, warnings: result.warnings, stats: result.stats };
  console.log('[PDF Render] done', { mode: o.mode, output: o.output, stats: result.stats, warnings: result.warnings });
  return { result, url: URL.createObjectURL(blob), size: blob.size, fontNotes: fonts?.notes ?? [] };
}

async function runGenerate(): Promise<void> {
  if (!currentLayout || !currentAnalysis || !currentPdfBytes || !currentFile || isGenerating) return;
  const mode: RenderMode = renderDebug.checked ? 'debug' : 'overlay';
  const output = currentExportMode();
  const range = renderRange.value as RenderRange;
  const { pages, unitIds, label, suffix } = rangeToPages(range);

  isGenerating = true;
  updateExportUi();
  setText(renderError, null);
  downloadLink.hidden = true;
  renderWarnings.hidden = true;
  if (generatedUrl) {
    URL.revokeObjectURL(generatedUrl);
    generatedUrl = null;
  }
  renderProgress.hidden = false;
  renderProgress.value = 0;
  const started = performance.now();

  try {
    const built = await buildPdf({
      pdf: { buffer: currentPdfBytes, analysis: currentAnalysis, layout: currentLayout },
      fileName: currentFile.name,
      entries,
      mode,
      output,
      pages,
      unitIds,
      onStatus: (message) => setText(renderStatus, message),
      onProgress: (p) => {
        renderProgress.value = p.percent;
        setText(renderStatus, `${p.stage}  Page ${p.page} / ${p.pageCount}  ${p.percent}%`);
      },
    });
    const { result } = built;
    if (mode === 'overlay') setText(fontNote, built.fontNotes.length ? built.fontNotes.join('\n') : null);
    if (mode === 'overlay') {
      lastRenderResult = result;
      renderTableDiagnostics(currentLayout, result);
      renderTypographyDiagnostics(currentLayout, result);
      renderLayoutRoleDiagnostics(currentLayout, result);
    }

    generatedUrl = built.url;
    const fileName = withOutputSuffix(result.fileName, suffix);
    downloadLink.href = generatedUrl;
    downloadLink.download = fileName;
    downloadLink.textContent = `Download ${fileName} (${formatBytes(built.size)})`;
    downloadLink.hidden = false;

    const seconds = ((performance.now() - started) / 1000).toFixed(1);
    const s = result.stats;
    const pagesWord = output === 'bilingual' ? 'spread(s)' : 'page(s)';
    setText(
      renderStatus,
      mode === 'debug'
        ? `Debug PDF ready (${label}): ${s.pagesRendered} ${pagesWord} with bounding boxes in ${seconds}s. ` +
            'Red = eligible block, blue = its lines, grey dashed = translated but not overlaid, green dashed = image, ' +
            'orange = table cell, purple = figure element (dashed: usable area)' +
            (roleDiagnosticsOverlay.checked ? ', teal = sidebar / callout container (dashed: inner area), magenta = structured abstract region / label' : '') +
            (output === 'bilingual' ? '; boxes are drawn on the right half only.' : '.')
        : `Done (${label}, ${output === 'bilingual' ? 'side-by-side' : 'translated only'}) in ${seconds}s: ` +
            `${s.pagesRendered} ${pagesWord}, ${s.unitsWritten} unit(s) written, ${s.masksDrawn} lines masked` +
            (s.masksDropped.CLIP ? ` (${s.masksDropped.CLIP} clipped away: original text visible)` : '') +
            ', ' +
            `${s.unitsSkipped} unit(s) skipped, ${result.warnings.length} layout warning(s)` +
            (s.tableCells ? `, table cells ${s.tableCellsWritten} written / ${s.tableCellsOverflow} kept in English` : '') +
            (s.figureCells ? `, figure elements ${s.figureCellsWritten} written / ${s.figureCellsOverflow} kept in English` : '') +
            (s.replacedChars ? `, ${s.replacedChars} character(s) not in any font` : '') +
            `. Fonts: ${result.fonts}; fallback glyphs: ${s.fontFallbackCount}.`,
    );
    showRenderWarnings(result);
  } catch (err) {
    console.error('[PDF Render] failed', err);
    const message =
      err instanceof RenderError || err instanceof FontLoadError
        ? err.message
        : err instanceof RangeError
          ? `The browser ran out of memory (${err.message}). Try a smaller page range.`
          : err instanceof Error
            ? err.message
            : String(err);
    setText(renderStatus, null);
    setText(renderError, `PDF generation failed: ${message}`);
  } finally {
    isGenerating = false;
    renderProgress.hidden = true;
    updateExportUi();
  }
}

generateBtn.addEventListener('click', () => void runGenerate());
renderDebug.addEventListener('change', updateExportUi);
exportMode.addEventListener('change', updateExportUi);

// ---------------------------------------------------------------------------
// User Mode: automatic analyze → translate → side-by-side bilingual PDF
// ---------------------------------------------------------------------------

type JobStep = 'read' | 'analyze' | 'scope' | 'confirm' | 'terminology' | 'translate' | 'qa' | 'generate' | 'done';
const JOB_STEPS: JobStep[] = ['read', 'analyze', 'scope', 'confirm', 'terminology', 'translate', 'qa', 'generate', 'done'];
/**
 * Before 開始翻譯 only the PDF is read and analysed, in the browser; the steps
 * that spend tokens are not shown yet, so the progress card cannot be mistaken
 * for a translation that already started.
 */
const ANALYSIS_STEPS: ReadonlySet<JobStep> = new Set<JobStep>(['read', 'analyze', 'scope']);

/** Failures a general user can act on; the message never contains technical details. */
class UserFacingError extends Error {
  constructor(message: string) {
    super(message);
    this.name = 'UserFacingError';
  }
}

const MSG_UNSUPPORTED = '目前只支援可選取文字的 PDF，掃描型 PDF 暫不支援。';
const MSG_READ_FAILED = '無法讀取這個 PDF，請確認檔案是否完整後再試一次。';
const MSG_PASSWORD = '這個 PDF 有密碼保護，目前無法處理。';
const MSG_TRANSLATE_FAILED = '翻譯服務暫時發生問題，請稍後再試。';
const MSG_TRANSLATE_RATE_LIMITED = '翻譯請求過於頻繁，請稍後再試。';
const MSG_PAYLOAD_TOO_LARGE = '文件內容過大，請改用較小的 PDF。';

/** Why the whole translation step produced nothing, in words a user can act on (401 is handled by lockApp). */
function translateFailureMessage(): string {
  const err = client.lastError;
  switch (err?.kind) {
    case 'network':
      return MSG_NETWORK;
    case 'rate_limit':
      return MSG_TRANSLATE_RATE_LIMITED;
    case 'payload_too_large':
      return MSG_PAYLOAD_TOO_LARGE;
    default:
      return MSG_TRANSLATE_FAILED; // 5xx, timeouts, invalid responses
  }
}
const MSG_GENERATE_FAILED = 'PDF 產生失敗，請重新嘗試。';
const MSG_OUT_OF_MEMORY = '這個 PDF 太大，瀏覽器記憶體不足，請關閉其他分頁後重新嘗試。';

function resetJobUi(): void {
  jobCard.hidden = true;
  jobReset.hidden = true;
  jobRescope.hidden = true;
  jobRetry.hidden = true;
  dropZone.hidden = false;
  uploadNote.hidden = false;
  jobProgress.hidden = false;
  jobDone.hidden = true;
  setText(jobNote, null);
  setText(jobOutput, null);
  jobUsageEl.hidden = true;
  jobUsageEl.replaceChildren();
  setText(jobError, null);
  jobDownload.removeAttribute('href');
  jobDownload.removeAttribute('download');
  jobBar.value = 0;
  jobPercent.textContent = '0%';
  jobTitle.textContent = '正在分析文件…';
  jobPhase.hidden = false;
  for (const li of jobSteps.querySelectorAll('li')) li.classList.remove('is-active', 'is-done');
}

function setJobProgress(step: JobStep, percent: number, label: string): void {
  jobCard.hidden = false;
  // While a file is processed the page shows only the file line and the progress.
  dropZone.hidden = true;
  uploadNote.hidden = true;
  const value = Math.max(jobBar.value, Math.min(100, Math.round(percent))); // never goes backwards
  jobBar.value = value;
  jobPercent.textContent = `${value}%`;
  jobStage.textContent = label;
  const analysing = ANALYSIS_STEPS.has(step);
  // 'confirm' (quote confirmed, payment states passing): no translation has started and no payment happened.
  jobTitle.textContent = analysing ? '正在分析文件…' : step === 'confirm' ? MSG_CONFIRMING : '正在翻譯文件…';
  jobPhase.hidden = !analysing;
  const current = JOB_STEPS.indexOf(step);
  for (const li of jobSteps.querySelectorAll<HTMLLIElement>('li')) {
    const stepName = li.dataset.step as JobStep;
    const index = JOB_STEPS.indexOf(stepName);
    li.hidden = analysing && !ANALYSIS_STEPS.has(stepName);
    li.classList.toggle('is-done', index < current || step === 'done');
    li.classList.toggle('is-active', index === current && step !== 'done');
  }
}

/**
 * How long translating took. The token count beside it is Developer Mode only
 * (?debug=true); User Mode never sees usage numbers.
 * Hidden when no provider reported usage, so no estimate is ever shown.
 */
function renderJobUsage(): void {
  const snapshot = jobUsage.snapshot();
  console.log('[Token Usage] job summary', snapshot);
  if (!jobUsage.hasUsage) {
    jobUsageEl.hidden = true;
    jobUsageEl.replaceChildren();
    return;
  }
  const seconds = Math.max(1, Math.round(snapshot.totalDurationMs / 1000));
  jobUsageEl.replaceChildren(
    ...(DEBUG_MODE
      ? [el('span', undefined, `本次 Token 使用量：${formatTokens(snapshot.totalProcessedTokens)}`)]
      : []),
    el('span', undefined, `翻譯時間：${seconds} 秒`),
  );
  jobUsageEl.hidden = false;
}

function showDownloadButton(url: string, fileName: string, failedUnits: number): void {
  setJobProgress('done', 100, '完成');
  jobProgress.hidden = true;
  jobDone.hidden = false;
  jobDownload.href = url;
  jobDownload.download = fileName;
  jobReset.hidden = false;
  jobRescope.hidden = false;
  setText(jobNote, failedUnits > 0 ? `有 ${failedUnits} 段文字未能翻譯，已保留英文原文。` : null);
  setText(jobOutput, lastOutput ? `輸出頁面：${lastOutput.label}` : null);
  renderJobUsage();
}

function friendlyError(step: JobStep, err: unknown): string {
  if (err instanceof UserFacingError) return err.message;
  if (err instanceof RangeError || (err instanceof RenderError && err.kind === 'MEMORY')) return MSG_OUT_OF_MEMORY;
  if (err instanceof Error && err.name === 'PasswordException') return MSG_PASSWORD;
  if (err instanceof RenderError && err.kind === 'ENCRYPTED') return MSG_PASSWORD;
  if (step === 'read' || step === 'analyze') return MSG_READ_FAILED;
  if (step === 'translate') return MSG_TRANSLATE_FAILED;
  return MSG_GENERATE_FAILED;
}

/**
 * User Mode, part 1: read + analyse the PDF (extraction → layout → tables →
 * figures → final logical units), detect chapters, then show the translation
 * scope and wait. Nothing is sent to the Worker here. Every await is
 * followed by a check that this job is still the current one, so a newer
 * file always wins.
 */
async function runAnalysisJob(file: File, job: Job): Promise<void> {
  const alive = () => job === currentJob && !job.controller.signal.aborted;
  let step: JobStep = 'read';
  lifecycle.startAnalysis();

  try {
    setJobProgress('read', 1, '正在讀取 PDF...');
    const pdf = await analyzePdf(file, (s) => {
      if (!alive()) return;
      if (s.step === 'read') setJobProgress('read', 2, '正在讀取 PDF...');
      else {
        step = 'analyze';
        const pct = s.step === 'layout' ? 18 : 5 + (s.total ? (12 * s.done) / s.total : 0);
        setJobProgress('analyze', pct, '正在分析 PDF...');
      }
    });
    if (!alive()) return;
    currentPdfBytes = pdf.buffer;
    currentAnalysis = pdf.analysis;
    currentLayout = pdf.layout;
    if (!pdf.analysis.hasSelectableText || !pdf.layout || pdf.layout.translationBlocks.length === 0) {
      throw new UserFacingError(MSG_UNSUPPORTED);
    }
    void loadFontSet().catch(() => undefined); // warm the font download while the user picks the scope
    setJobProgress('scope', 19, '請選擇翻譯範圍');
    jobCard.hidden = true;
    showScopeCard();
  } catch (err) {
    if (!alive()) return;
    console.error(`[User Mode] ${step} failed`, err);
    jobProgress.hidden = true;
    jobCard.hidden = false;
    jobReset.hidden = false;
    setText(jobError, friendlyError(step, err));
  } finally {
    if (currentJob === job) currentJob = null;
  }
}

/** Document + scope fingerprints of a resolved scope: what the gate compares the confirmed quote with. */
function jobContextFor(scope: ScopeResolution & { ok: true }): JobContext {
  if (!currentLayout) throw new Error('job: no analysed document');
  return { documentFingerprint: documentFingerprint(currentLayout.translationBlocks), scopeFingerprint: scope.fingerprint };
}

/**
 * confirmQuote() → beginPayment() → verifyPayment(), each a lifecycle
 * transition, none of them a provider call. Returns true when the job holds a
 * confirmed quote for this exact document + scope and a CONFIRMED payment:
 * a retry of a failed run returns true at once (no second confirmation, no
 * second payment). An expired quote goes back to the scope card, re-quoted.
 * PAYMENT_MODE = 'disabled': the two payment steps pass immediately, marked
 * as a bypass; the user only ever sees 「正在確認翻譯資訊…」.
 */
async function ensureQuoteConfirmedAndPaid(scope: ScopeResolution & { ok: true }): Promise<boolean> {
  lifecycle.observeContext(jobContextFor(scope)); // a changed document / scope drops the old confirmation first
  // A finished run spent its entitlement: the same scope again starts from a fresh quote (setQuote handles COMPLETED).
  if (lifecycle.status === 'COMPLETED') lifecycle.setQuote(quoteForScope(scope));
  const state = lifecycle.current;
  if (state.confirmedQuote && state.payment?.status === 'CONFIRMED' && state.entitlement?.status === 'ACTIVE') return true;

  if (!state.confirmedQuote) {
    const confirmed = lifecycle.confirmQuote();
    if (!confirmed.ok) {
      backToScope(confirmed.reason === 'quote_expired' ? MSG_QUOTE_EXPIRED : null);
      return false;
    }
  }
  try {
    await lifecycle.beginPayment(paymentProvider);
    await lifecycle.verifyPayment(paymentProvider);
  } catch (err) {
    if (err instanceof QuoteExpiredError) {
      backToScope(MSG_QUOTE_EXPIRED);
      return false;
    }
    throw err;
  }
  const payment = lifecycle.current.payment;
  if (payment?.status !== 'CONFIRMED') {
    // FAILED / CANCELLED is final for this quote: drop it so the next attempt starts from a fresh quote and order.
    // PENDING (a future mode still verifying) keeps the state; the next click verifies again.
    if (payment && payment.status !== 'PENDING') lifecycle.invalidate('payment_failed');
    backToScope(MSG_PAYMENT_NOT_CONFIRMED);
    return false;
  }
  return true;
}

/** Back to the scope card with a fresh quote (and, when given, one message under it). */
function backToScope(message: string | null): void {
  jobCard.hidden = true;
  scopeCard.hidden = false;
  updateScopeUi(); // re-quotes
  if (message) setText(scopeError, message);
}

/**
 * User Mode, part 2 (確認價格並繼續): the lifecycle in order —
 *   confirmQuote() → beginPayment() → verifyPayment() → startTranslation()
 * — then the translation job. The click handler never calls translate itself.
 */
async function runPaidTranslationFlow(scope: ScopeResolution & { ok: true }): Promise<void> {
  if (!currentLayout) return;
  scopeCard.hidden = true;
  resetJobUi();
  setJobProgress('confirm', 19, MSG_CONFIRMING);
  if (!(await ensureQuoteConfirmedAndPaid(scope))) return;
  await runTranslationJob(scope);
}

/**
 * User Mode, part 3: terminology → translation → QA of the selected units
 * only, then the complete side-by-side PDF: the Chinese overlay on the
 * selected units, the original English everywhere else, no page dropped.
 * Another scope of the same PDF reuses the analysis, the chapter map and the
 * translation cache; the file is never read or analysed again.
 * Enters through lifecycle.startTranslation(): the gate (confirmed quote, not
 * expired, same document, same scope, payment CONFIRMED, entitlement ACTIVE)
 * must pass before the first request.
 */
async function runTranslationJob(scope: ScopeResolution & { ok: true }): Promise<void> {
  if (!currentPdfBytes || !currentAnalysis || !currentLayout || !currentFile) return;
  const start = lifecycle.startTranslation(jobContextFor(scope));
  if (!start.ok) {
    backToScope(gateFailureMessage(start.gate));
    return;
  }
  const job = startJob(); // fresh abort controller and token counters for this scope run
  const pdf: AnalyzedPdf & { layout: LayoutResult } = { buffer: currentPdfBytes, analysis: currentAnalysis, layout: currentLayout };
  const file = currentFile;
  const alive = () => job === currentJob && !job.controller.signal.aborted;
  const jobEntries = entries; // shared per PDF: units translated for an earlier scope stay cached here
  // Export page selection, decided before the run so the download line and the file name agree with it.
  const output = resolveOutputPages(scope.scope, chapterDetection?.chapters ?? [], currentAnalysis.pageCount, {
    keepFullDocument: scopeFull.checked,
  });
  lastScope = scope;
  lastOutput = output;
  renderScopeDevPanel(scope);
  // The quote the user confirmed (immutable snapshot); validated against actual usage afterwards.
  const quote = start.confirmed.snapshot;
  scopeCard.hidden = true;
  let step: JobStep = 'translate';

  try {
    const blocks = scope.units;
    setJobProgress('terminology', 20, '正在分析專業術語...');
    const stats = await translateAll(blocks, jobEntries, {
      signal: job.controller.signal,
      scopeKey: scope.fingerprint,
      onStage: (stage) => {
        if (!alive()) return;
        if (stage === 'terminology') setJobProgress('terminology', 20, '正在分析專業術語...');
        else if (stage === 'translate') setJobProgress('translate', 24, '正在翻譯...');
        else setJobProgress('qa', 74, '正在檢查翻譯品質...');
      },
      onProgress: (p) => {
        if (!alive() || p.blocksTotal === 0) return;
        setJobProgress('translate', 24 + (50 * (p.blocksDone + p.blocksFailed)) / p.blocksTotal, '正在翻譯...');
      },
      onQaProgress: (done, total) => {
        if (!alive() || total === 0) return;
        setJobProgress('qa', 74 + (7 * done) / total, '正在檢查翻譯品質...');
      },
    });
    if (!alive()) return;
    recordPricingValidation(quote, stats, !start.retry);
    let translated = 0;
    for (const b of blocks) {
      const e = jobEntries.get(b.id);
      if (e && (e.status === 'done' || e.status === 'cached') && e.translation) translated++;
    }
    console.log('[Translation]', Object.fromEntries(jobEntries));
    if (translated === 0) throw new UserFacingError(translateFailureMessage());

    step = 'generate';
    lifecycle.startRendering();
    setJobProgress('generate', 82, '正在產生中英對照 PDF...');
    const built = await buildPdf({
      pdf,
      fileName: file.name,
      entries: jobEntries,
      mode: 'overlay',
      output: 'bilingual',
      // Whole document for 整份論文 and for 保留完整 PDF, otherwise exactly the pages of the scope.
      pages: output.pages ? new Set(output.pages) : null,
      // Only this run's units are overlaid. An earlier scope's translations stay in the cache
      // (no new API call if they are selected again) but are never silently added to this file.
      unitIds: new Set(blocks.map((u) => u.id)),
      onStatus: () => undefined,
      onProgress: (p) => {
        if (alive()) setJobProgress('generate', 84 + (15 * p.percent) / 100, '正在產生中英對照 PDF...');
      },
    });
    if (!alive()) {
      URL.revokeObjectURL(built.url);
      return;
    }
    if (built.fontNotes.length) console.warn('[fonts]', built.fontNotes.join('\n'));
    if (generatedUrl) URL.revokeObjectURL(generatedUrl); // the previous scope run's PDF
    generatedUrl = built.url; // revoked by resetExport() when the next file is chosen
    lifecycle.complete();
    showDownloadButton(built.url, withOutputSuffix(built.result.fileName, output.fileSuffix), stats.failedBlocks);
  } catch (err) {
    if (!alive()) return;
    console.error(`[User Mode] ${step} failed`, err);
    lifecycle.fail(err instanceof Error ? err.message : String(err)); // confirmed quote + payment stay: 重試 needs neither again
    jobProgress.hidden = true;
    jobCard.hidden = false;
    jobReset.hidden = false;
    jobRescope.hidden = false;
    jobRetry.hidden = false;
    setText(jobError, friendlyError(step, err));
  } finally {
    if (currentJob === job) currentJob = null;
  }
}

/** 重試 after a failed run: same confirmed quote, same payment; only the gate is evaluated again. */
jobRetry.addEventListener('click', () => {
  if (!lastScope?.ok) return;
  void runPaidTranslationFlow(lastScope);
});

// ---------------------------------------------------------------------------
// Translation scope (both modes): whole document / chapters / page range.
// Chapter detection and scope resolution run in the browser on the final
// logical units (scope/*.ts); no token is spent before 開始翻譯.
// ---------------------------------------------------------------------------

const MSG_SCOPE_EMPTY = '選取的範圍沒有可翻譯的內容。';

function resetScopeUi(): void {
  chapterDetection = null;
  checkedChapterIds = new Set();
  chapterTree.replaceChildren();
  scopeChapterCount.textContent = '';
  scopeStartInput.value = '';
  scopeEndInput.value = '';
  scopeStartInput.removeAttribute('max');
  scopeEndInput.removeAttribute('max');
  scopePageTotal.textContent = '';
  for (const r of scopeModeInputs) r.checked = r.value === 'all';
  scopeChaptersPanel.hidden = true;
  scopePagesPanel.hidden = true;
  scopeFull.checked = false;
  scopeFullWrap.hidden = true;
  scopeOutput.textContent = '';
  lastOutput = null;
  setText(scopeError, null);
  scopeCard.hidden = true;
  scopeDevSummary.textContent = 'Translation Scope';
  scopeDevList.textContent = '';
}

function scopeMode(): TranslationScope['mode'] {
  const checked = scopeModeInputs.find((r) => r.checked)?.value;
  return checked === 'chapters' || checked === 'pages' ? checked : 'all';
}

/** The scope as the controls describe it; page numbers are validated by resolveTranslationScope(). */
function currentScope(): TranslationScope {
  switch (scopeMode()) {
    case 'chapters':
      return { mode: 'chapters', chapterIds: [...checkedChapterIds] };
    case 'pages':
      return { mode: 'pages', startPage: Number(scopeStartInput.value.trim()), endPage: Number(scopeEndInput.value.trim()) };
    default:
      return { mode: 'all' };
  }
}

/** Resolve the scope on the final logical units; null before the analysis. */
function resolveCurrentScope(): ScopeResolution | null {
  if (!currentLayout || !currentAnalysis) return null;
  return resolveTranslationScope(currentLayout.translationBlocks, chapterDetection?.chapters ?? [], currentScope(), {
    pageCount: currentAnalysis.pageCount,
    blocks: currentLayout.blocks,
    pages: currentAnalysis.pages,
  });
}

/** After the analysis: detect chapters (browser-side), fill the controls, show the card. */
function showScopeCard(): void {
  if (!currentAnalysis || !currentLayout) return;
  chapterDetection = detectChapters(currentAnalysis, currentLayout);
  checkedChapterIds = new Set();
  renderChapterTree(chapterDetection);
  const total = currentAnalysis.pageCount;
  scopeStartInput.max = String(total);
  scopeEndInput.max = String(total);
  scopeStartInput.value = '1';
  scopeEndInput.value = String(total);
  scopePageTotal.textContent = `PDF 共 ${total} 頁（PDF 頁序，非印刷頁碼）`;
  for (const r of scopeModeInputs) r.checked = r.value === 'all';
  scopeCard.hidden = false;
  console.log('[Translation Scope] chapters', chapterDetection);
  lifecycle.analysisDone();
  updateScopeUi();
}

function renderChapterTree(detection: ChapterDetection): void {
  chapterTree.replaceChildren();
  if (detection.source === 'none' || detection.chapters.length === 0) {
    scopeChapterCount.textContent = detection.warnings[0] ?? '此 PDF 無法可靠偵測章節，請改用整份論文或自訂頁數。';
    chapterTree.hidden = true;
    return;
  }
  chapterTree.hidden = false;
  scopeChapterCount.textContent = `偵測到 ${detection.chapters.length} 個章節`;
  const ids = new Set(detection.chapters.map((c) => c.id));
  const byParent = new Map<string, ChapterInfo[]>();
  const roots: ChapterInfo[] = [];
  for (const c of detection.chapters) {
    if (c.parentId && ids.has(c.parentId)) {
      const list = byParent.get(c.parentId) ?? [];
      list.push(c);
      byParent.set(c.parentId, list);
    } else roots.push(c);
  }
  const build = (list: ChapterInfo[]): HTMLUListElement => {
    const ul = el('ul');
    for (const c of list) {
      const li = el('li');
      const label = el('label');
      const box = el('input') as HTMLInputElement;
      box.type = 'checkbox';
      box.value = c.id;
      box.dataset.id = c.id;
      box.addEventListener('change', () => onChapterToggle(c.id, box.checked));
      label.appendChild(box);
      label.appendChild(el('span', 'chapter-title', c.title));
      label.appendChild(el('span', 'chapter-pages', chapterPageLabel(c)));
      li.appendChild(label);
      const children = byParent.get(c.id);
      if (children?.length) li.appendChild(build(children));
      ul.appendChild(li);
    }
    return ul;
  };
  chapterTree.appendChild(build(roots));
}

function chapterBox(id: string): HTMLInputElement | null {
  return chapterTree.querySelector<HTMLInputElement>(`input[data-id="${CSS.escape(id)}"]`);
}

/** Standard checkbox tree: a parent ticks its subtree; children set their parent to all / none / indeterminate. */
function onChapterToggle(id: string, on: boolean): void {
  if (!chapterDetection) return;
  const chapters = chapterDetection.chapters;
  for (const sub of chapterSubtreeIds(chapters, id)) {
    const box = chapterBox(sub);
    if (!box) continue;
    box.checked = on;
    box.indeterminate = false;
    if (on) checkedChapterIds.add(sub);
    else checkedChapterIds.delete(sub);
  }
  let parentId = chapters.find((c) => c.id === id)?.parentId;
  while (parentId) {
    const pid = parentId;
    const children = chapters.filter((c) => c.parentId === pid).map((c) => chapterBox(c.id));
    const all = children.length > 0 && children.every((b) => b?.checked);
    const some = children.some((b) => b?.checked || b?.indeterminate);
    const box = chapterBox(pid);
    if (box) {
      box.checked = all;
      box.indeterminate = !all && some;
    }
    if (all) checkedChapterIds.add(pid);
    else checkedChapterIds.delete(pid);
    parentId = chapters.find((c) => c.id === pid)?.parentId;
  }
  updateScopeUi();
}

/** Validate the controls, show the one error line, enable 開始翻譯, refresh the Developer Mode panel. */
function updateScopeUi(): void {
  const mode = scopeMode();
  scopeChaptersPanel.hidden = mode !== 'chapters';
  scopePagesPanel.hidden = mode !== 'pages';
  const resolution = resolveCurrentScope();
  let error: string | null = null;
  if (resolution && !resolution.ok) {
    const noChapters = !chapterDetection || chapterDetection.source === 'none';
    // Nothing ticked yet is "not ready", not an error; the unavailable-chapters text is already shown in the list.
    error = mode === 'chapters' && (noChapters || checkedChapterIds.size === 0) ? null : resolution.error;
  } else if (resolution && resolution.ok && resolution.units.length === 0) {
    error = MSG_SCOPE_EMPTY;
  }
  setText(scopeError, error);
  scopeStartBtn.disabled = !resolution || !resolution.ok || resolution.units.length === 0 || isTranslating;

  // Export page selection: only offered for a partial scope; the whole document is always written for 整份論文.
  scopeFullWrap.hidden = mode === 'all';
  if (mode === 'all') scopeFull.checked = false;
  const output = currentOutputPages();
  scopeOutput.textContent = output && !error ? `輸出頁面：${output.label}` : '';
  renderScopeDevPanel(resolution);
  // Every scope change re-quotes: same engine for 整份論文 / 選擇章節 / 自訂頁數, no API call.
  renderQuote(resolution && resolution.ok && !error ? resolution : null);
}

/** The pages the download will contain for the controls as they stand; null before the analysis. */
function currentOutputPages(): OutputPages | null {
  if (!currentAnalysis) return null;
  return resolveOutputPages(currentScope(), chapterDetection?.chapters ?? [], currentAnalysis.pageCount, {
    keepFullDocument: scopeFull.checked,
  });
}

for (const r of scopeModeInputs) r.addEventListener('change', updateScopeUi);
scopeFull.addEventListener('change', updateScopeUi);
scopeStartInput.addEventListener('input', updateScopeUi);
scopeEndInput.addEventListener('input', updateScopeUi);
scopeStartBtn.addEventListener('click', () => {
  const scope = resolveCurrentScope();
  if (!scope || !scope.ok || scope.units.length === 0) {
    updateScopeUi();
    return;
  }
  void runPaidTranslationFlow(scope);
});
scopeReset.addEventListener('click', () => fileInput.click());
jobRescope.addEventListener('click', () => {
  jobCard.hidden = true;
  scopeCard.hidden = false;
  updateScopeUi();
});

/** Developer Mode: mode, chapter source, detected / selected chapters, counts, fingerprint, ownership check. */
function scopeDevLines(res: ScopeResolution | null): string[] {
  const d = chapterDetection;
  const lines: string[] = [];
  if (!res) return ['(analyze a PDF first)'];
  lines.push(`mode:                     ${res.scope.mode}`);
  lines.push(`chapter source:           ${d ? d.source : '—'}${d && d.source !== 'none' ? ` (${d.chapters.length} detected)` : ''}`);
  if (res.scope.mode === 'pages') lines.push(`selected page range:      ${res.scope.startPage}–${res.scope.endPage}`);
  if (res.scope.mode === 'chapters') {
    const wanted = new Set(res.scope.chapterIds);
    const titles = (d?.chapters ?? []).filter((c) => wanted.has(c.id)).map((c) => `${c.title} (${c.id})`);
    lines.push(`selected chapters:        ${titles.length ? titles.join(' | ') : '—'}`);
  }
  lines.push(`scope fingerprint:        ${res.fingerprint}`);
  if (!res.ok) {
    lines.push(`resolve error:            ${res.error}`);
  } else {
    const s = res.stats;
    lines.push(
      `selected units:           ${s.selectedUnits} / ${s.totalUnits} logical units  (pages ${s.selectedPages.length ? `${s.selectedPages[0]}–${s.selectedPages[s.selectedPages.length - 1]}` : '—'})`,
      `  BODY ${s.body} · HEADING/TITLE ${s.heading} · CAPTION ${s.caption} · FOOTNOTE ${s.footnote} · TABLE_CELL ${s.tableCells} · FIGURE ${s.figureUnits} · other ${s.other}`,
      `provider-bound units:     ${s.providerBound}  (selected units minus untranslatable text)`,
      `selected chars:           ${s.selectedChars.toLocaleString()}`,
      `boundary-expanded units:  ${s.boundaryExpanded}${s.boundaryExpanded ? `  [${[...res.boundaryExpandedIds].slice(0, 12).join(', ')}${res.boundaryExpandedIds.size > 12 ? ', …' : ''}]` : ''}`,
      `numeric-only skipped:     ${s.numericSkipped}  (table / figure cells inside the scope, never sent)`,
      `duplicate source items:   ${s.duplicateSourceItems}  (provider-bound selected units; must be 0)`,
    );
  }
  if (d && d.chapters.length) {
    lines.push('', 'detected chapters:');
    for (const c of d.chapters) {
      const anchor = `p.${c.startPage}${c.startY !== undefined ? ` y=${Math.round(c.startY)}` : ''} → p.${c.endPage ?? c.startPage}${c.endY !== undefined ? ` y=${Math.round(c.endY)}` : ' end'}`;
      lines.push(`  ${'  '.repeat(c.level - 1)}[${c.level}] ${c.title}  ${chapterPageLabel(c)}  (${c.id}, ${c.source}, ${anchor}${c.references ? ', references' : ''})`);
    }
  }
  if (d?.warnings.length) lines.push('', ...d.warnings.map((w) => `warning: ${w}`));
  return lines;
}

function renderScopeDevPanel(res: ScopeResolution | null = lastScope ?? resolveCurrentScope()): void {
  (window as unknown as { __translationScope: ScopeResolution | null; __chapters: ChapterDetection | null }).__translationScope = res;
  (window as unknown as { __chapters: ChapterDetection | null }).__chapters = chapterDetection;
  if (!DEBUG_MODE) return;
  scopeDevSummary.textContent = res?.ok
    ? `Translation Scope: ${res.fingerprint} · ${res.stats.selectedUnits} / ${res.stats.totalUnits} units · ${res.stats.boundaryExpanded} boundary-expanded · dup ${res.stats.duplicateSourceItems}`
    : 'Translation Scope';
  scopeDevList.textContent = scopeDevLines(res).join('\n');
}

/** Cost panel: the scope of the run that just finished plus its cache / skip counts. */
function scopeRunLines(r: DocumentTranslationResult): string[] {
  const res = lastScope;
  if (!res || !res.ok) return ['scope:                   whole document (no scope resolution recorded)'];
  const s = res.stats;
  return [
    `scope:                   ${res.fingerprint} (${res.scope.mode})`,
    `selected units:          ${s.selectedUnits} / ${s.totalUnits}  (BODY ${s.body}, TABLE_CELL ${s.tableCells}, FIGURE ${s.figureUnits}, CAPTION ${s.caption})`,
    `selected chars:          ${s.selectedChars.toLocaleString()}`,
    `provider-bound units:    ${s.providerBound}`,
    `cache hits:              ${r.translation.cachedBlocks}`,
    `numeric skipped:         ${s.numericSkipped}`,
    `duplicate source items:  ${s.duplicateSourceItems}`,
    `boundary expanded:       ${s.boundaryExpanded}`,
    `terminology:             ${r.terminology.cached ? 'reused (scope cache)' : `${r.terminology.requests} request(s)`} · ${r.terminology.samples} samples / ${r.terminology.sampleChars.toLocaleString()} chars`,
  ];
}

// ---------------------------------------------------------------------------
// Quote Engine + pricing validation (pricing/*.ts).
// The quote is built in the browser from the selected final logical units and
// the same provider-bound gate the translation pipeline uses; it makes no
// request and spends no token. User Mode sees the character count and the
// price only. Developer Mode sees the estimate, the post-run validation
// against the provider's actual usage, the local history and the CSV export.
// ---------------------------------------------------------------------------

/** Quote for a resolved scope (the object main.ts hands to the pipeline). */
function quoteForScope(res: ScopeResolution & { ok: true }): QuoteSnapshot {
  if (!currentLayout || !currentAnalysis) throw new Error('quote: no analysed document');
  return buildQuote({
    units: res.units,
    documentUnits: currentLayout.translationBlocks,
    scope: res.scope,
    scopeFingerprint: res.fingerprint,
    selectedPages: res.stats.selectedPages,
    pageCount: currentAnalysis.pageCount,
  });
}

/** The confirmed, still usable quote of exactly this scope, if the job holds one; null otherwise. */
function lockedQuoteFor(res: ScopeResolution & { ok: true }): QuoteSnapshot | null {
  const cq = lifecycle.current.confirmedQuote;
  if (!cq || !currentLayout || lifecycle.status === 'COMPLETED') return null;
  const locked = cq.scopeFingerprint === res.fingerprint && cq.documentFingerprint === documentFingerprint(currentLayout.translationBlocks);
  return locked ? cq.snapshot : null;
}

function resetQuoteUi(): void {
  quoteEl.hidden = true;
  quoteChars.textContent = '';
  quotePrice.textContent = '';
  quoteNote.textContent = '';
  setText(quoteTestPhase, null);
  quoteDev.hidden = true;
  quoteDevList.textContent = '';
  pricingValidationPanel.hidden = true;
  pricingValidationList.textContent = '';
  (window as unknown as { __quote?: unknown; __pricingValidation?: unknown }).__quote = undefined;
  (window as unknown as { __pricingValidation?: unknown }).__pricingValidation = undefined;
}

/** Show the quote of the scope as the controls stand; hidden when the scope is not valid. */
function renderQuote(res: (ScopeResolution & { ok: true }) | null): void {
  if (!res || res.units.length === 0) {
    quoteEl.hidden = true;
    quoteDev.hidden = true;
    (window as unknown as { __quote?: unknown }).__quote = undefined;
    lifecycle.setQuote(null);
    return;
  }
  // While nothing is confirmed every scope change simply re-quotes. A confirmed quote of another
  // scope / document is invalidated by setQuote() (back to QUOTE_READY) and must be confirmed again;
  // the confirmed quote of this same scope (a failed run awaiting 重試) is shown as it was confirmed.
  const quote = lockedQuoteFor(res) ?? quoteForScope(res);
  lifecycle.setQuote(quote);
  (window as unknown as { __quote?: unknown }).__quote = quote;
  const text = userQuoteText(quote, { testPhase: PAYMENT_MODE === 'disabled' });
  quotePriceLabel.textContent = text.priceLabel;
  quotePrice.textContent = text.price;
  quoteChars.textContent = text.chars;
  quoteNote.textContent = text.note;
  setText(quoteTestPhase, text.testPhase);
  quoteDetailSummary.textContent = text.detailSummary;
  quoteDetailText.textContent = text.detail;
  quoteEl.hidden = false;
  if (DEBUG_MODE) {
    quoteDevSummary.textContent = `定價估算（開發模式）：${formatInt(quote.billableChars)} 字元 · ${text.price} · ${quote.billableUnits} 單元 · ${quote.scopeFingerprint}`;
    quoteDevList.textContent = quoteDevLines(quote).join('\n');
    quoteDev.hidden = false;
  }
}

/**
 * After a run: compare the quote with the job's actual provider usage
 * (JobUsage, the authoritative accounting), show 定價驗證 and, when the
 * provider was really called, append a statistics-only record to the local
 * history. Never touches the translation result.
 */
function recordPricingValidation(quote: QuoteSnapshot, stats: TranslationStats, store: boolean): void {
  const usage = jobUsage.snapshot();
  const validation: PricingValidation = validateQuote(quote, usage, {
    unitsTotal: stats.blocksTotal,
    skippedUnits: stats.skippedBlocks,
    cachedUnits: stats.cachedBlocks,
    translatedUnits: stats.translatedBlocks,
    failedUnits: stats.failedBlocks,
  });
  (window as unknown as { __pricingValidation?: unknown }).__pricingValidation = validation;
  console.log('[Pricing] validation', validation);
  // A run served entirely from the cache spent nothing: shown, but not a data point for the estimate.
  if (store && jobUsage.hasUsage) {
    pricingHistory.add(toRecord(validation, quote));
  }
  if (!DEBUG_MODE) return;
  pricingValidationSummary.textContent =
    `定價驗證：${validation.safetyResult} · 售價 NT$${validation.suggestedPrice} · 實際 API 成本 NT$${validation.actual.actualApiCostTwd.toFixed(2)} · 毛利率 ${(validation.grossMarginRate * 100).toFixed(1)}%`;
  pricingValidationList.textContent = validationLines(validation, quote).join('\n');
  pricingValidationPanel.hidden = false;
  pricingSection.hidden = false;
  renderPricingHistory();
}

/** Developer Mode: the newest 50 validation records plus their summary. */
function renderPricingHistory(): void {
  if (!DEBUG_MODE) return;
  const records = pricingHistory.list();
  const summary = summarizeHistory(records);
  pricingHistorySummary.textContent = `定價測試紀錄：樣本數 ${summary.samples} · LOSS ${summary.lossCount}`;
  pricingHistoryList.textContent = historyLines(records, summary).join('\n');
  pricingHistoryInfo.textContent = `最近 ${records.length} 筆（最多 50 筆，僅統計資料，不含 PDF 文字）`;
  pricingExportBtn.disabled = records.length === 0;
  pricingClearBtn.disabled = records.length === 0;
  pricingSection.hidden = false;
}

/** Developer Mode: 付款模式 / Quote 狀態 / Payment / verification / ids / gate (flow/format.ts). */
function renderFlowDevPanel(): void {
  const state = lifecycle.current;
  (window as unknown as { __job?: unknown }).__job = state;
  if (!DEBUG_MODE) return;
  flowDevSummaryEl.textContent = flowDevSummary(state, PAYMENT_MODE);
  flowDevList.textContent = flowDevLines(state, PAYMENT_MODE).join('\n');
  flowDev.hidden = false;
}

lifecycle.subscribe(renderFlowDevPanel);
renderFlowDevPanel();

pricingExportBtn.addEventListener('click', () => {
  const records = pricingHistory.list();
  if (records.length === 0) return;
  const blob = new Blob([historyToCsv(records)], { type: 'text/csv;charset=utf-8' });
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = `pricing-validation-${new Date().toISOString().slice(0, 10)}.csv`;
  document.body.appendChild(a);
  a.click();
  a.remove();
  setTimeout(() => URL.revokeObjectURL(url), 1000);
});

pricingClearBtn.addEventListener('click', () => {
  if (!window.confirm('清除本機的定價測試紀錄？此動作無法復原。')) return;
  pricingHistory.clear();
  renderPricingHistory();
});

if (DEBUG_MODE) renderPricingHistory();
